<template>
  <form ref="form" method="post" @submit.prevent>
    <input type="hidden" name="_token" :value="csrfToken" />
    <div class="l-page l-page--message-edit-condition rm-add-cond">
      <main class="l-main">
        <!-- パンくずリスト -->
        <breadcrumb :breadcrumb-data="breadcrumbData" />
        <!-- /パンくずリスト -->

        <div class="l-main-head">
          <div class="c-title u-w420">
            <div class="c-title__text c-title__text--blue u-fs34">配信条件の設定</div>
          </div>
          <div class="c-progress">
            <ul class="c-progress-list">
              <li class="c-progress-list__item">メッセージの作成</li>
              <li class="c-progress-list__item is-current">配信条件の設定</li>
              <li class="c-progress-list__item">配信内容の確認</li>
              <li class="c-progress-list__item">設定完了</li>
            </ul>
          </div>
        </div>

        <div class="l-main-content rm-add-cond__content">
          <div class="split-container">
            <!-- 左: エディタ (メッセージ内容編集ページと同様、セクション毎に独立した panel を縦積みする) -->
            <div class="editor-stack">
              <div class="panel panel-editor">
                <div class="panel-body">
                  <!-- パネル見出し (このパネルの主題) -->
                  <div class="section-question">
                    <h2>リマインダー配信を予約するページ</h2>
                    <div class="section-desc">リマインダー配信の予約条件を設定します</div>
                  </div>

                  <!-- トリガー方式選択 (予約のみに影響するサブセクション) -->
                  <div class="section-question section-question--sub">
                    <h3>1. どちらでリマインダー配信の予約をしますか？</h3>
                    <div class="section-desc">通知配信を発火させるきっかけを選択してください</div>
                  </div>
                  <div class="trigger-selector">
                    <div class="trigger-card" :class="{ selected: trigger === 'auto' }" @click="setTrigger('auto')">
                      <div class="trigger-card-title">条件に合致したら自動予約</div>
                      <div class="trigger-card-desc">設定した条件を満たすユーザーに自動で通知を送ります</div>
                    </div>
                    <div class="trigger-card" :class="{ selected: trigger === 'manual' }" @click="setTrigger('manual')">
                      <div class="trigger-card-title">任意のタイミングで予約</div>
                      <div class="trigger-card-desc">
                        カート追加のボタンクリックなど、サイト側が指定したタイミングで予約します。実装が必要です。
                      </div>
                    </div>
                  </div>

                  <template v-if="trigger === 'auto'">
                    <!-- 予約対象 (サブセクション: トリガー毎に説明/入力を切替) -->
                    <div class="section-question section-question--sub">
                      <h3>2. どこのページで予約しますか？</h3>
                      <ul class="section-desc section-desc-list">
                        <li>指定したページ(URL)にアクセスしたユーザーへのリマインダー配信を予約します</li>
                        <li>ユーザーが既に除外するページにアクセスしている場合は予約されません</li>
                      </ul>
                      <button class="url-help-link" type="button" @click="isUrlHelpOpen = true">
                        「等しい」「含む」などのURL一致の判定方法について
                        <span class="url-help-icon">?</span>
                      </button>
                    </div>
                    <div class="schedule-section-block">
                      <div v-if="false" class="category-block-head">
                        <div class="cat-title">ページ行動履歴</div>
                        <div class="cat-desc">指定したページ(URL)にアクセスされたユーザーに通知を配信します</div>
                        <!-- root operator (set) は or 固定運用のため非表示。実装は将来の切り戻しに備えて残す。 -->
                        <div v-if="false" class="operator-radio-row">
                          <label class="operator-radio" :class="{ selected: schedule.operator === 'and' }">
                            <input
                              type="radio"
                              name="schedule-operator"
                              value="and"
                              :checked="schedule.operator === 'and'"
                              @change="setRootOperator('schedule', 'and')"
                            />
                            <span class="operator-radio-dot"></span>
                            <span class="operator-radio-label">全ての条件を満たす</span>
                          </label>
                          <label class="operator-radio" :class="{ selected: schedule.operator === 'or' }">
                            <input
                              type="radio"
                              name="schedule-operator"
                              value="or"
                              :checked="schedule.operator === 'or'"
                              @change="setRootOperator('schedule', 'or')"
                            />
                            <span class="operator-radio-dot"></span>
                            <span class="operator-radio-label">いずれかの条件を満たす</span>
                          </label>
                        </div>
                      </div>
                      <div class="category-block-body">
                        <template v-if="schedule">
                          <condition-group-node :group="schedule" :is-root="true" context-root="schedule" />
                        </template>
                        <template v-else>
                          <div class="schedule-empty">
                            <div class="schedule-empty-icon">⊘</div>
                            予約するページは設定されていません
                          </div>
                          <button class="add-btn-schedule" type="button" @click="addSchedule">
                            <span class="circle-plus">
                              <svg
                                version="1.1"
                                xmlns="http://www.w3.org/2000/svg"
                                xmlns:xlink="http://www.w3.org/1999/xlink"
                              >
                                <use xlink:href="/assets/img/sprite.svg#plus" />
                              </svg>
                            </span>
                            予約するページの追加
                          </button>
                        </template>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <!-- trigger=manual: 「予約するページ」の URL 条件入力は行わず、
                         サイト側スクリプトで発火する運用方式である旨と、
                         次画面 (配信内容の確認) でコピペ用スクリプトが自動生成されることを案内する。 -->
                    <div class="section-question section-question--sub">
                      <h3>2. どこのページで予約しますか？</h3>
                      <ul class="section-desc section-desc-list">
                        <li>リマインダー配信の予約は、サイト側に設置するスクリプトから発火させます</li>
                      </ul>
                    </div>
                    <div class="manual-trigger-notice">
                      <p class="manual-trigger-notice__p">
                        任意のタイミングで配信の予約をするには、ユーザー行動 (例: フォーム離脱、カート追加など) を検知したタイミングで JavaScript コードを実行してください。
                      </p>
                      <p class="manual-trigger-notice__p">
                        実装方法の詳細は次のステップ「配信内容の確認」に表示されるサンプルスクリプトをご確認ください。
                      </p>
                    </div>
                  </template>
                </div>
              </div>

              <!-- ブロック: 除外 (解除するページ) -->
              <div class="panel panel-editor">
                <div class="panel-body">
                  <div class="section-question unschedule">
                    <h2>リマインダー配信を解除するページ</h2>
                    <ul class="section-desc section-desc-list">
                      <li>指定したページ(URL)にアクセスしたユーザーへのリマインダー配信を解除します</li>
                      <li>この解除するページにアクセス後、再度予約するページにアクセスした場合は<u>再度予約されます</u></li>
                    </ul>
                  </div>
                  <div class="unschedule-section-block">
                    <div v-if="false" class="category-block-head">
                      <div class="cat-title">ページ行動履歴</div>
                      <div class="cat-desc">指定したページ(URL)にアクセスされたユーザーへの配信予約は解除されます</div>
                      <!-- root operator (unset) は or 固定運用のため非表示。実装は将来の切り戻しに備えて残す。 -->
                      <div v-if="false && unschedule" class="operator-radio-row unschedule">
                        <label class="operator-radio" :class="{ selected: unschedule.operator === 'and' }">
                          <input
                            type="radio"
                            name="unschedule-operator"
                            value="and"
                            :checked="unschedule.operator === 'and'"
                            @change="setRootOperator('unschedule', 'and')"
                          />
                          <span class="operator-radio-dot"></span>
                          <span class="operator-radio-label">全ての条件を満たす</span>
                        </label>
                        <label class="operator-radio" :class="{ selected: unschedule.operator === 'or' }">
                          <input
                            type="radio"
                            name="unschedule-operator"
                            value="or"
                            :checked="unschedule.operator === 'or'"
                            @change="setRootOperator('unschedule', 'or')"
                          />
                          <span class="operator-radio-dot"></span>
                          <span class="operator-radio-label">いずれかの条件を満たす</span>
                        </label>
                      </div>
                    </div>
                    <div class="category-block-body">
                      <div v-if="false" class="unschedule-hint">
                        <span class="hint-icon">!</span>
                        <div>
                          <strong>例: コンバージョンしたユーザーへの配信予約の解除</strong>
                          完了ページにアクセスしたユーザーへの配信予約を解除して送らないようにします
                        </div>
                      </div>
                      <template v-if="unschedule">
                        <condition-group-node :group="unschedule" :is-root="true" context-root="unschedule" />
                      </template>
                      <template v-else>
                        <div class="unschedule-empty">
                          <div class="unschedule-empty-icon">⊘</div>
                          解除するページは設定されていません
                        </div>
                        <button class="add-btn-unschedule" type="button" @click="addUnschedule">
                          <span class="circle-plus">
                            <svg
                              version="1.1"
                              xmlns="http://www.w3.org/2000/svg"
                              xmlns:xlink="http://www.w3.org/1999/xlink"
                            >
                              <use xlink:href="/assets/img/sprite.svg#plus" />
                            </svg>
                          </span>
                          解除するページの追加
                        </button>
                      </template>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ブロック: 配信除外リスト (suppress) -->
              <div class="panel panel-editor">
                <div class="panel-body">
                  <div class="section-question suppress">
                    <h2>リマインダー配信から除外するページ</h2>
                    <ul class="section-desc section-desc-list">
                      <li>指定したページ(URL)にアクセスしたユーザーはリマインダー配信から除外されます</li>
                      <li>
                        この除外するページにアクセス後、再度予約するページにアクセスした場合も<u>予約されません</u>
                      </li>
                    </ul>
                  </div>
                  <div class="suppress-section-block">
                    <div class="category-block-body">
                      <template v-if="suppress">
                        <condition-group-node :group="suppress" :is-root="true" context-root="suppress" />
                      </template>
                      <template v-else>
                        <div class="suppress-empty">
                          <div class="suppress-empty-icon">⊘</div>
                          除外するページは設定されていません
                        </div>
                        <button class="add-btn-suppress" type="button" @click="addSuppress">
                          <span class="circle-plus">
                            <svg
                              version="1.1"
                              xmlns="http://www.w3.org/2000/svg"
                              xmlns:xlink="http://www.w3.org/1999/xlink"
                            >
                              <use xlink:href="/assets/img/sprite.svg#plus" />
                            </svg>
                          </span>
                          除外するページの追加
                        </button>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="viewer-stack">
              <!-- 右: ビューア -->
              <div class="panel panel-viewer">
                <div class="viewer-head">
                  <div class="viewer-title">
                    <span class="eye-icon">◉</span>
                    条件プレビュー
                  </div>
                </div>
                <div ref="viewerBody" class="viewer-body">
                  <div v-if="!hasAnyNonEmpty" class="v-full-empty">
                    <div class="v-full-empty-icon">◎</div>
                    <div class="v-full-empty-title">まだページが設定されていません</div>
                    <div class="v-full-empty-desc">
                      左側で条件を入力すると、<br />
                      ここに設定内容のプレビューが表示されます
                    </div>
                  </div>
                  <template v-else>
                    <div v-if="scheduleNonEmptyCount > 0" class="v-summary-block">
                      <div class="v-summary-label">条件概要</div>
                      <div class="v-summary-text">
                        以下を満たすユーザーに配信されます<br />
                        <span v-html="scheduleSummaryHtml"></span>
                      </div>
                    </div>

                    <div class="v-tree-label">ツリー構造</div>

                    <!-- trigger=manual は「予約するページ」を CMS 側で持たない運用のため、
                         セクション枠は残しつつ body は空状態表示に固定する
                         (内部 schedule に auto 時の入力が残っていてもプレビューには反映しない)。 -->
                    <div class="v-section schedule">
                      <div class="v-section-head">予約するページ</div>
                      <div class="v-section-body">
                        <template v-if="trigger === 'auto' && hasStructure(schedule)">
                          <div v-html="renderViewerGroupHtml(schedule)"></div>
                        </template>
                        <div v-else-if="trigger === 'manual'" class="v-section-empty">
                          任意のタイミングでスクリプトから予約
                        </div>
                        <div v-else class="v-section-empty">予約するページは設定されていません</div>
                      </div>
                    </div>

                    <div class="v-section unschedule">
                      <div class="v-section-head">解除するページ</div>
                      <div class="v-section-body">
                        <template v-if="unschedule && hasStructure(unschedule)">
                          <div v-html="renderViewerGroupHtml(unschedule)"></div>
                        </template>
                        <div v-else class="v-section-empty">解除するページは設定されていません</div>
                      </div>
                    </div>

                    <div class="v-section suppress">
                      <div class="v-section-head">除外するページ</div>
                      <div class="v-section-body">
                        <template v-if="suppress && hasStructure(suppress)">
                          <div v-html="renderViewerGroupHtml(suppress)"></div>
                        </template>
                        <div v-else class="v-section-empty">除外するページは設定されていません</div>
                      </div>
                    </div>
                  </template>
                </div>
              </div>

              <!-- 配信設定パターン例カード (autoの場合のみ) -->
              <div v-if="trigger === 'auto'" class="panel panel-templates" :class="{ open: patternExamplesOpen }">
                <div class="templates-head">
                  <div class="templates-title">配信設定パターン例</div>
                  <div class="templates-desc">よくある配信パターンの設定例を紹介します</div>
                </div>
                <div class="templates-body">
                  <div class="templates-content-wrap">
                    <div class="templates-grid">
                      <div
                        v-for="tpl in templateDefs"
                        :key="tpl.key"
                        class="template-card"
                        @click="applyTemplate(tpl.key)"
                      >
                        <div class="template-name">{{ tpl.name }}</div>
                        <div class="template-desc">{{ tpl.desc }}</div>
                      </div>
                    </div>
                    <div v-if="!patternExamplesOpen" class="templates-toggle-container">
                      <button class="templates-toggle-btn" type="button" @click="patternExamplesOpen = true">
                        配信設定パターン例をすべて表示する
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- アクションバー -->
    <div class="p-message-predict p-message-predict--no-number">
      <div class="p-message-predict__content">
        <div class="p-message-predict-suspend">
          <div class="p-message-predict-suspend__items">
            <button class="c-btn c-btn--block u-w146 u-fs24" type="button" @click="onQuitClick">
              <p class="c-btn__text">作成をやめる</p>
            </button>
          </div>
        </div>
        <div class="p-message-predict__btns">
          <button class="c-btn c-btn--gray u-w320 u-h50 u-fs20" type="button" @click="onBackClick">
            <p class="c-btn__text">メッセージ作成に戻る</p>
          </button>
          <button
            class="c-btn c-btn--blue u-w320 u-h50 u-fs20"
            :class="{ 'is-loading': isLoadingConfirmBtn }"
            :disabled="isLoadingConfirmBtn"
            type="button"
            @click="onConfirmClick"
          >
            <p class="c-btn__text">内容の確認画面へ</p>
          </button>
        </div>
      </div>
    </div>

    <!-- URL条件ヘルプモーダル -->
    <div class="rm-add-cond url-help-modal" :class="{ 'is-open': isUrlHelpOpen }" aria-hidden="true">
      <div class="url-help-modal-overlay" @click="isUrlHelpOpen = false"></div>
      <div class="url-help-modal-panel">
        <div class="url-help-modal-header">
          <div class="url-help-modal-title">URL条件の一致方法</div>
          <button
            class="url-help-modal-close"
            type="button"
            aria-label="閉じる"
            @click="isUrlHelpOpen = false"
          >
            ×
          </button>
        </div>
        <div class="url-help-modal-body">
          <div v-for="item in urlHelpItems" :key="item.name" class="url-help-item">
            <div class="url-help-item-head">
              <div class="url-help-item-name">{{ item.name }}</div>
              <div class="url-help-item-desc">{{ item.desc }}</div>
            </div>
            <div class="url-help-item-examples">
              <div class="url-help-setting">
                <span class="url-help-setting-label">設定値:</span>
                <span class="url-help-chip brand">{{ item.setting }}</span>
              </div>
              <div v-for="(ex, i) in item.examples" :key="i" class="url-help-row">
                <span class="url-help-url" v-html="ex.urlHtml"></span>
                <span class="url-help-match" :class="ex.match ? 'match' : 'nomatch'">
                  {{ ex.match ? '○ 一致' : '× 不一致' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <error-modal :is-active="isError" :message="errorMessage" :on-close-click="onCloseErrorModal" />

    <!-- trigger=auto で『予約するページ』が未設定のまま保存しようとした際の最終確認モーダル。
         仕様: 空保存は「自動でのリマインダー配信予約を行わない」設定と等価。エラーではなく警告として同意を取る。 -->
    <control-modal
      classes="c-modal--common"
      title="リマインダー配信を予約するページが未設定です"
      body="「予約するページ」を未設定のまま保存すると、自動でリマインダー配信が予約されなくなります。このまま保存してもよろしいですか？"
      cancel-button-label="戻って編集する"
      enter-button-label="このまま保存"
      :is-active="isEmptyConditionConfirm"
      :on-close-click="onCancelEmptyConditionConfirm"
      :on-cancel-click="onCancelEmptyConditionConfirm"
      :on-enter-click="onConfirmEmptyConditionConfirm"
    />

    <!-- 配信設定パターン例カード押下時に、設定内容を表示するだけのモーダル。
         タイトル 2 行目はテンプレート名、本文はカード内の灰色説明テキスト (tpl.desc) を転記する。 -->
    <control-modal
      classes="c-modal--common c-modal--pattern-confirm"
      :title="patternConfirmTitle"
      :body="patternConfirmBody"
      cancel-button-label="閉じる"
      :is-active="isPatternConfirm"
      :on-close-click="cancelApplyTemplate"
      :on-cancel-click="cancelApplyTemplate"
    />
  </form>
</template>

<script>
// リマインダーの配信条件設定ページ。
// 左側にルールのツリー型エディタ、右側にリアルタイムプレビューを表示する。
// ネストグループ対応の条件ツリーは ConditionGroupNode を再帰的に用いて描画する。
// 現時点ではバックエンド保存処理は未接続で画面モックのみ。
export default {
  provide: function () {
    var self = this;
    return {
      // 再帰コンポーネントから reactive に参照するルート。
      // errors は nodeId をキーにしたフィールド単位エラーマップ。
      condRoot: self,
      // 再帰コンポーネントからinjectするアクション集合。
      // refを経由して最新の関数参照を解決することで循環参照を避ける。
      condActions: {
        toggleOperator: function (id) {
          self.toggleOperator(id);
        },
        ungroup: function (id) {
          self.ungroup(id);
        },
        deleteNode: function (id) {
          self.deleteNode(id);
        },
        addPattern: function (id) {
          self.addPattern(id);
        },
        addGroup: function (id) {
          self.addGroup(id);
        },
        toggleMethodSelect: function (id) {
          self.toggleMethodSelect(id);
        },
        setMethod: function (id, m) {
          self.setMethod(id, m);
        },
        updateUrl: function (id, v) {
          self.updateUrl(id, v);
        },
        toggleRefine: function (id) {
          self.toggleRefine(id);
        },
        clearRefine: function (id) {
          self.clearRefine(id);
        },
        refineAdjust: function (id, field, delta, defaultStart) {
          self.refineAdjust(id, field, delta, defaultStart);
        },
        refineUpdate: function (id, field, v) {
          self.refineUpdate(id, field, v);
        },
        toggleQuery: function (id) {
          self.toggleQuery(id);
        },
        addQuery: function (id) {
          self.addQuery(id);
        },
        deleteQuery: function (id, qpId) {
          self.deleteQuery(id, qpId);
        },
        updateQueryKey: function (id, qpId, v) {
          self.updateQueryKey(id, qpId, v);
        },
        updateQueryValue: function (id, qpId, v) {
          self.updateQueryValue(id, qpId, v);
        },
        updateQueryMethod: function (id, qpId, v) {
          self.updateQueryMethod(id, qpId, v);
        },
        // 条件カードとビューワー側リーフのホバー連動用。
        isHovered: function (id) {
          return self.hoveredPatternId === id;
        },
        setHovered: function (id) {
          self.hoveredPatternId = id;
        },
      },
    };
  },
  props: {
    csrfToken: String,
    conditionInfo: Object,
  },
  data: function () {
    var info = this.conditionInfo || {};
    var initialPattern = this._createPattern();
    return {
      idCounter: 0,
      trigger: 'auto',
      patternExamplesOpen: false,
      // root operator (set/unset/exclude) は or 固定運用 (radio 非表示)。デフォルト or で初期化する。
      schedule: this._createGroup('or', [initialPattern]),
      unschedule: null,
      // 配信除外リスト (suppress)。バックエンド未対応のためリロードで消える。
      // API とは exclude キー (受信は condition_exclude) で対応付ける。
      suppress: null,
      isUrlHelpOpen: false,
      hoveredPatternId: null,
      isLoadingConfirmBtn: false,
      isError: false,
      errorMessage: '',
      // trigger=auto + schedule 実質空の際の最終確認モーダル活性状態。
      isEmptyConditionConfirm: false,
      // 配信設定パターン例 (templateDefs) のカードをクリックした時に
      // 誤操作で既存条件を上書きしないよう事前確認するモーダル活性状態。
      isPatternConfirm: false,
      // 確認モーダルで「設定する」が押された時に適用するパターン key。
      pendingTemplateKey: null,
      // フィールド単位バリデーションエラー: { [nodeId]: { url?, 'query.<qpId>.key'?, 'query.<qpId>.value'? } }
      // 値は c-error--balloon に表示する短文メッセージ。submit 前検証のみで入力中検証はしない(ユーザー操作の即時クリアは updater で実施)。
      errors: {},
      breadcrumbData: [
        { title: '配信メッセージの一覧', url: info.listUrl || '' },
        {
          title: '配信メッセージの編集',
          url: (info.editUrl || '') + '?type=edit&message_id=' + (info.messageId || ''),
        },
        { title: '配信条件の設定', url: '' },
      ],
      // summary: 確認モーダルで desc の下に表示する「このパターンで反映される設定内容」の短文。
      //         改行を含むため本文側は white-space: pre-line で表示する。
      templateDefs: [
        {
          key: 'dropout',
          name: 'フォーム離脱された方への通知',
          desc: 'カート、会員登録、お問い合わせなど、特定のページを訪れたが完了ページに到達していないユーザーに再訪を促します',
          summary: [
            '※ [予約するページ] に訪問後、 [解除するページ] に到達していないユーザーにリマインドしたい場合に有効です',
            '※ ユーザーが [解除するページ] に到達後、再び [予約するページ] に訪問すると、改めて配信予約されます',
            '',
            'こちらの場合は、下記のような配信設定になります',
            '',
            '[リマインダー配信を予約するページ]',
            ' ・URL: 「https://example.com/cart」を含む',
            '',
            '[リマインダー配信を解除するページ]',
            ' ・URL: 「https://example.com/complete」を含む',
            '',
            '[リマインダー配信から除外するページ]',
            ' （設定なし）',
          ].join('\n'),
        },
        {
          key: 'product',
          name: '商品ページを閲覧された方へクーポンを通知',
          desc: '商品ページを閲覧し購入を検討中のユーザーに、クーポンページへ誘導する通知を配信することで購入を後押しします',
          summary: [
            '※ ユーザーが [除外するページ] に到達後、再び [予約するページ] に訪問しても配信予約されなくなります',
            '',
            'こちらの場合は、下記のような配信設定になります',
            '',
            '[リマインダー配信を予約するページ]',
            ' ・URL: 「https://example.com/product/」を含む',
            '',
            '[リマインダー配信を解除するページ]',
            ' （設定なし）',
            '',
            '[リマインダー配信から除外するページ]',
            ' ・URL: 「https://example.com/order」を含む',
          ].join('\n'),
        },
        {
          key: 'utm',
          name: 'UTM付きURLへのアクセス',
          desc: 'メールのリンクやキャンペーン経由のリンクなどUTMパラメーター付きリンクから訪問したユーザーに配信します',
          summary: [
            '※ ユーザーが特定のクエリストリング(?utm_source=mailなど)でアクセスした時にだけリマインドしたい場合にご使用ください',
            '',
            'こちらの場合は、下記のような配信設定になります',
            '',
            '[リマインダー配信を予約するページ]',
            ' ・URL: 「https://example.com/」を含む',
            ' ・クエリストリング: キー「utm_source」 が 値「mail」と等しい',
          ].join('\n'),
        },
      ],
      // URL 条件の一致方法ヘルプ用の項目定義。
      // URL 条件は eq/contains の 2 種に絞り込んだため、'等しくない' / '含まない' の
      // カードは非表示。復活させる場合は下記コメントを外す。
      urlHelpItems: [
        {
          name: '等しい',
          desc: 'ユーザーがアクセスしたURLと一致させる場合',
          setting: 'https://example.com/pages/1',
          examples: [
            {
              urlHtml: '<span class="url-help-chip brand">https://example.com/pages/1</span>',
              match: true,
            },
            {
              urlHtml: '<span class="url-help-chip brand">https://example.com/pages/1/</span>',
              match: true,
            },
            { urlHtml: 'https://example.com/pages/2', match: false },
          ],
        },
        /*
        {
            name: '等しくない',
            desc: 'ユーザーがアクセスしたURLと完全に一致しない場合',
            setting: 'https://example.com/pages/1',
            examples: [
                { urlHtml: '<span class="url-help-chip brand">https://example.com/pages/1</span>', match: false },
                { urlHtml: 'https://example.com/pages/2', match: true },
            ],
        },
        */
        {
          name: '含む（前方一致）',
          desc: 'ユーザーがアクセスしたURLの前方に一致すればよい場合',
          setting: 'https://example.com/pages/',
          examples: [
            {
              urlHtml: '<span class="url-help-chip brand inline">https://example.com/pages/</span>',
              match: true,
            },
            {
              urlHtml: '<span class="url-help-chip brand inline">https://example.com/pages/</span>1',
              match: true,
            },
            {
              urlHtml: '<span class="url-help-chip brand inline">https://example.com/pages/</span>2',
              match: true,
            },
            { urlHtml: 'https://example.com/pagestats', match: false },
            { urlHtml: 'https://example.com/article/1', match: false },
          ],
        },
        {
          name: '含む（部分一致）',
          desc: 'ユーザーがアクセスしたURLの一部に一致すればよい場合',
          setting: '/pages/',
          examples: [
            {
              urlHtml: 'https://example.com<span class="url-help-chip brand inline">/pages/</span>1',
              match: true,
            },
            {
              urlHtml: 'https://example.com<span class="url-help-chip brand inline">/pages/</span>2',
              match: true,
            },
            { urlHtml: 'https://example.com/pagestats', match: false },
            { urlHtml: 'https://example.com/article/1', match: false },
          ],
        },
        /*
        {
            name: '含まない',
            desc: 'ユーザーがアクセスしたURLの一部にも一致しない場合',
            setting: '/page',
            examples: [
                { urlHtml: 'https://example.com<span class="url-help-chip brand inline">/page</span>/1', match: false },
                { urlHtml: 'https://example.com/article/1', match: true },
            ],
        },
        */
      ],
    };
  },
  computed: {
    scheduleNonEmptyCount: function () {
      // trigger=manual は「予約するページ」を CMS 側で持たない運用のため、
      // 内部 schedule に auto 時の入力が残っていてもプレビュー・空条件判定では 0 件扱いにする。
      if (this.trigger === 'manual') return 0;
      return this.countNonEmptyLeaves(this.schedule);
    },
    unscheduleNonEmptyCount: function () {
      return this.unschedule ? this.countNonEmptyLeaves(this.unschedule) : 0;
    },
    suppressNonEmptyCount: function () {
      return this.suppress ? this.countNonEmptyLeaves(this.suppress) : 0;
    },
    hasAnyNonEmpty: function () {
      return this.scheduleNonEmptyCount > 0 || this.unscheduleNonEmptyCount > 0 || this.suppressNonEmptyCount > 0;
    },
    scheduleSummaryHtml: function () {
      return this.generateSummaryHtml(this.schedule);
    },
    // 配信設定パターン適用前の確認モーダルで表示するタイトル/本文。
    // タイトルは "配信設定パターン{index}\n{name}" の 2 行で CSS 側の white-space: pre-line で改行する。
    pendingTemplate: function () {
      var self = this;
      if (!this.pendingTemplateKey) return null;
      var idx = this.templateDefs.findIndex(function (t) {
        return t.key === self.pendingTemplateKey;
      });
      if (idx < 0) return null;
      return { index: idx, def: this.templateDefs[idx] };
    },
    patternConfirmTitle: function () {
      var p = this.pendingTemplate;
      return p ? p.def.name : '';
    },
    // モーダル本文は「補足テキスト(desc) → 設定内容」を空行で区切って結合する。
    // white-space: pre-line により \n\n が段落区切りとして描画される。
    patternConfirmBody: function () {
      var p = this.pendingTemplate;
      if (!p) return '';
      return p.def.desc + '\n\n' + p.def.summary;
    },
  },
  created: function () {
    // 既存の配信条件(v1 JSONB)が Controller から渡っている場合、
    // trigger / schedule / unschedule を初期化し直して編集継続できるようにする。
    var info = this.conditionInfo || {};
    if (info.existingCondition) {
      this._loadExistingCondition(info.existingCondition);
    }

    // グローバルクリックで c-select の _selectOpen を閉じる用リスナ登録
    this._docClickHandler = this._handleDocumentClick.bind(this);
    document.addEventListener('click', this._docClickHandler);
    this._docKeydownHandler = this._handleDocumentKeydown.bind(this);
    document.addEventListener('keydown', this._docKeydownHandler);
  },
  mounted: function () {
    // ビューワー側リーフのホバーを検知しエディタの条件カードと相互ハイライトする。
    // v-html で再描画される要素へ個別にイベントを張るのは難しいため、委譲で処理する。
    var self = this;
    this._viewerOverHandler = function (e) {
      var leaf = e.target.closest && e.target.closest('.v-leaf[data-source]');
      if (leaf) self.hoveredPatternId = leaf.getAttribute('data-source');
    };
    this._viewerOutHandler = function (e) {
      var leaf = e.target.closest && e.target.closest('.v-leaf[data-source]');
      if (leaf) self.hoveredPatternId = null;
    };
    if (this.$refs.viewerBody) {
      this.$refs.viewerBody.addEventListener('mouseover', this._viewerOverHandler);
      this.$refs.viewerBody.addEventListener('mouseout', this._viewerOutHandler);
    }
  },
  beforeUnmount: function () {
    document.removeEventListener('click', this._docClickHandler);
    document.removeEventListener('keydown', this._docKeydownHandler);
    if (this.$refs.viewerBody) {
      this.$refs.viewerBody.removeEventListener('mouseover', this._viewerOverHandler);
      this.$refs.viewerBody.removeEventListener('mouseout', this._viewerOutHandler);
    }
  },
  methods: {
    // --- ID採番 / ノード生成ヘルパー ---
    _genId: function () {
      this.idCounter = (this.idCounter || 0) + 1;
      return 'id_' + this.idCounter;
    },
    _createPattern: function (url, method) {
      this.idCounter = (this.idCounter || 0) + 1;
      return {
        id: 'id_' + this.idCounter,
        type: 'pattern',
        url: url || '',
        // URL 条件のデフォルトは 'eq' (等しい)。従来は 'contains' だった。
        // method: method || 'contains',
        method: method || 'eq',
        seconds: null,
        scroll: null,
        refineOpen: false,
        queryParams: [],
        queryOpen: false,
        _selectOpen: false,
        _methodJustSelected: false,
      };
    },
    _createQueryParam: function (key, method, value) {
      this.idCounter = (this.idCounter || 0) + 1;
      return {
        id: 'id_' + this.idCounter,
        key: key || '',
        // QueryString 条件のデフォルトは 'eq' (等しい)。従来は 'contains' だった。
        // method: method || 'contains',
        method: method || 'eq',
        value: value || '',
      };
    },
    _createGroup: function (operator, children) {
      this.idCounter = (this.idCounter || 0) + 1;
      return {
        id: 'id_' + this.idCounter,
        type: 'group',
        operator: operator || 'and',
        children: children || [],
      };
    },

    // --- ツリー探索 ---
    findNode: function (root, targetId) {
      if (!root) return null;
      if (root.id === targetId) return root;
      if (root.type !== 'group') return null;
      for (var i = 0; i < root.children.length; i++) {
        var found = this.findNode(root.children[i], targetId);
        if (found) return found;
      }
      return null;
    },
    findAnywhere: function (targetId) {
      return (
        this.findNode(this.schedule, targetId) ||
        (this.unschedule && this.findNode(this.unschedule, targetId)) ||
        (this.suppress && this.findNode(this.suppress, targetId)) ||
        null
      );
    },
    findParent: function (root, targetId) {
      if (!root || root.type !== 'group') return null;
      for (var i = 0; i < root.children.length; i++) {
        if (root.children[i].id === targetId) return root;
        var found = this.findParent(root.children[i], targetId);
        if (found) return found;
      }
      return null;
    },
    findRootContext: function (targetId) {
      if (this.findNode(this.schedule, targetId)) return 'schedule';
      if (this.unschedule && this.findNode(this.unschedule, targetId)) return 'unschedule';
      if (this.suppress && this.findNode(this.suppress, targetId)) return 'suppress';
      return null;
    },
    countNonEmptyLeaves: function (node) {
      if (!node) return 0;
      if (node.type === 'pattern') return node.url && node.url.trim() ? 1 : 0;
      var self = this;
      return node.children.reduce(function (sum, c) {
        return sum + self.countNonEmptyLeaves(c);
      }, 0);
    },
    hasStructure: function (node) {
      if (!node) return false;
      if (node.type === 'pattern') return !!(node.url && node.url.trim());
      if (node.children.length >= 2) return true;
      var self = this;
      return node.children.some(function (c) {
        return self.hasStructure(c);
      });
    },

    // --- トリガー / テンプレ ---
    setTrigger: function (trigger) {
      this.trigger = trigger;
      // トリガー切替でバリデーション対象が変わるため、表示中の検証状態は破棄する
      // (次回 Submit 時に現在の trigger に応じて再評価される)
      this.errors = {};
      this.isError = false;
      this.errorMessage = '';
      this.isEmptyConditionConfirm = false;
    },
    // カードクリックでは即時適用せず、確認モーダルに遷移させる。
    // 既存条件を編集中に誤って上書きしてしまうのを防ぐため。
    applyTemplate: function (key) {
      this.pendingTemplateKey = key;
      this.isPatternConfirm = true;
    },
    cancelApplyTemplate: function () {
      this.isPatternConfirm = false;
      this.pendingTemplateKey = null;
    },

    // --- ルート演算子 ---
    setRootOperator: function (context, value) {
      if (context === 'schedule' && this.schedule) {
        this.schedule.operator = value;
      } else if (context === 'unschedule' && this.unschedule) {
        this.unschedule.operator = value;
      } else if (context === 'suppress' && this.suppress) {
        this.suppress.operator = value;
      }
    },

    // --- ノード操作 ---
    toggleOperator: function (id) {
      var node = this.findAnywhere(id);
      if (node && node.type === 'group') {
        node.operator = node.operator === 'and' ? 'or' : 'and';
      }
    },
    ungroup: function (id) {
      var ctx = this.findRootContext(id);
      if (!ctx) return;
      var root = this[ctx];
      var parent = this.findParent(root, id);
      if (parent) {
        var idx = parent.children.findIndex(function (c) {
          return c.id === id;
        });
        if (idx < 0) return;
        var grp = parent.children[idx];
        var args = [idx, 1].concat(grp.children);
        Array.prototype.splice.apply(parent.children, args);
      }
    },
    deleteNode: function (id) {
      (this);
      function removeFrom(node) {
        if (!node || node.type !== 'group') return false;
        var idx = node.children.findIndex(function (c) {
          return c.id === id;
        });
        if (idx >= 0) {
          node.children.splice(idx, 1);
          return true;
        }
        for (var i = 0; i < node.children.length; i++) {
          if (removeFrom(node.children[i])) return true;
        }
        return false;
      }
      removeFrom(this.schedule);
      if (this.unschedule) removeFrom(this.unschedule);
      if (this.suppress) removeFrom(this.suppress);

      if (this.unschedule && this.unschedule.children.length === 0) {
        this.unschedule = null;
      }
      if (this.suppress && this.suppress.children.length === 0) {
        this.suppress = null;
      }
      if (this.schedule && this.schedule.children.length === 0) {
        this.schedule = null;
      }
    },
    addPattern: function (groupId) {
      var node = this.findAnywhere(groupId);
      if (node && node.type === 'group') {
        node.children.push(this._createPattern());
      }
    },
    addGroup: function (groupId) {
      var node = this.findAnywhere(groupId);
      if (node && node.type === 'group') {
        node.children.push(this._createGroup('and', [this._createPattern()]));
      }
    },
    addUnschedule: function () {
      // root operator (unset) は or 固定運用 (radio 非表示)。デフォルト or で初期化する。
      this.unschedule = this._createGroup('or', [this._createPattern()]);
    },
    addSchedule: function () {
      // root operator (set) は or 固定運用 (radio 非表示)。デフォルト or で初期化する。
      this.schedule = this._createGroup('or', [this._createPattern()]);
    },
    addSuppress: function () {
      // root operator (exclude) は or 固定運用 (radio 非表示)。デフォルト or で初期化する。
      this.suppress = this._createGroup('or', [this._createPattern()]);
    },

    // --- パターン個別の操作 ---
    updateUrl: function (id, value) {
      var node = this.findAnywhere(id);
      if (node) {
        node.url = value;
      }
      this._clearError(id, 'url');
    },
    toggleMethodSelect: function (id) {
      var node = this.findAnywhere(id);
      if (!node) return;
      this._closeAllMethodSelects(id);
      node._selectOpen = !node._selectOpen;
    },
    setMethod: function (id, method) {
      var node = this.findAnywhere(id);
      if (node) {
        node.method = method;
        node._selectOpen = false;
        // フル URL 入力時に例示 (.method-example) の連結が不自然になるため、
        // 一時的に例示表示を無効化する。復活させる場合は下記コメントを外す。
        // node._methodJustSelected = true;
      }
    },
    _closeAllMethodSelects: function (exceptId) {
      function walk(n) {
        if (!n) return;
        if (n.type === 'pattern' && n.id !== exceptId) {
          n._selectOpen = false;
        } else if (n.type === 'group') {
          n.children.forEach(walk);
        }
      }
      walk(this.schedule);
      walk(this.unschedule);
      walk(this.suppress);
    },
    toggleRefine: function (id) {
      var node = this.findAnywhere(id);
      if (node) {
        node.refineOpen = !node.refineOpen;
      }
    },
    clearRefine: function (id) {
      var node = this.findAnywhere(id);
      if (node) {
        node.seconds = null;
        node.scroll = null;
      }
    },
    refineAdjust: function (id, field, delta, defaultStart) {
      var node = this.findAnywhere(id);
      if (!node) return;
      // 空値から +/- を押した初回は delta を無視してデフォルト開始値を入れる。
      if (node[field] == null || node[field] === '') {
        node[field] = defaultStart;
        return;
      }
      var current = Number(node[field]);
      if (isNaN(current)) return;
      // seconds / scroll ともに下限 5 / 上限 100 にクランプ。
      var next = Math.max(5, Math.min(100, current + delta));
      node[field] = next;
    },
    refineUpdate: function (id, field, value) {
      var node = this.findAnywhere(id);
      if (!node) return;
      if (value === '' || value == null) {
        node[field] = null;
      } else if (/^\d+$/.test(String(value))) {
        // UI 上は readonly (+/- ボタンのみ) だが、念のため範囲 5〜100 にクランプ。
        node[field] = Math.max(5, Math.min(100, parseInt(value, 10)));
      }
    },
    toggleQuery: function (id) {
      var node = this.findAnywhere(id);
      if (!node) return;
      node.queryOpen = !node.queryOpen;
      if (node.queryOpen && node.queryParams.length === 0) {
        node.queryParams.push(this._createQueryParam());
      }
    },
    addQuery: function (id) {
      var node = this.findAnywhere(id);
      if (node) {
        node.queryParams.push(this._createQueryParam());
      }
    },
    deleteQuery: function (id, qpId) {
      var node = this.findAnywhere(id);
      if (!node) return;
      // 残り 1 件の状態で × を押されたら、空にしたうえで「クエリストリングで絞り込む」自体を閉じる。
      // (空 1 行の表示が残ると意味のない入力欄が残ったままになるため)
      if (node.queryParams.length === 1) {
        var qp = node.queryParams[0];
        qp.key = '';
        qp.value = '';
        qp.method = 'eq';
        // 既に発生していた key / value のフィールドエラーは表示し続ける意味が無いので一緒にクリア。
        this._clearError(id, 'query.' + qp.id + '.key');
        this._clearError(id, 'query.' + qp.id + '.value');
        node.queryOpen = false;
      } else {
        node.queryParams = node.queryParams.filter(function (q) {
          return q.id !== qpId;
        });
        this._clearError(id, 'query.' + qpId + '.key');
        this._clearError(id, 'query.' + qpId + '.value');
      }
    },
    updateQueryKey: function (id, qpId, value) {
      var node = this.findAnywhere(id);
      if (!node) return;
      var qp = node.queryParams.find(function (q) {
        return q.id === qpId;
      });
      if (qp) {
        qp.key = value;
      }
      this._clearError(id, 'query.' + qpId + '.key');
    },
    updateQueryValue: function (id, qpId, value) {
      var node = this.findAnywhere(id);
      if (!node) return;
      var qp = node.queryParams.find(function (q) {
        return q.id === qpId;
      });
      if (qp) {
        qp.value = value;
      }
      this._clearError(id, 'query.' + qpId + '.value');
    },
    updateQueryMethod: function (id, qpId, value) {
      var node = this.findAnywhere(id);
      if (!node) return;
      var qp = node.queryParams.find(function (q) {
        return q.id === qpId;
      });
      if (qp) {
        qp.method = value;
      }
    },

    // --- ビューア描画（v-html用にHTML文字列を生成） ---
    _escape: function (text) {
      return String(text == null ? '' : text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },
    _methodLabelShort: function (method) {
      var map = { eq: '=', notEq: '≠', contains: '⊇', notContains: '⊉' };
      return map[method] || method;
    },
    _methodLabelLeaf: function (method) {
      var map = { eq: '等しい', notEq: '等しくない', contains: '含む', notContains: '含まない' };
      return map[method] || method;
    },
    renderViewerGroupHtml: function (group) {
      if (!group) return '';
      var self = this;
      function leafHtml(pattern) {
        var hasUrl = pattern.url && pattern.url.trim().length > 0;
        var hasRefine = pattern.seconds !== null || pattern.scroll !== null;
        var validQP = pattern.queryParams.filter(function (q) {
          return q.key && q.key.trim();
        });
        var qpHtml = '';
        if (validQP.length > 0) {
          qpHtml =
            '<div class="v-leaf-qps">' +
            validQP
              .map(function (qp) {
                return (
                  '<span class="v-leaf-qp">?' +
                  self._escape(qp.key) +
                  '<span class="v-leaf-qp__op">' +
                  self._methodLabelShort(qp.method) +
                  '</span>' +
                  self._escape(qp.value) +
                  '</span>'
                );
              })
              .join('') +
            '</div>';
        }
        var extrasHtml = '';
        if (hasRefine) {
          extrasHtml =
            '<div class="v-leaf-extras">' +
            (pattern.seconds !== null ? '<span class="extra">⏱' + pattern.seconds + 's</span>' : '') +
            (pattern.scroll !== null ? '<span class="extra">⇵' + pattern.scroll + '%</span>' : '') +
            '</div>';
        }
        var leafClass = 'v-leaf' + (self.hoveredPatternId === pattern.id ? ' highlighted' : '');
        return (
          '<div class="v-node">' +
          '<div class="' +
          leafClass +
          '" data-source="' +
          self._escape(pattern.id) +
          '">' +
          '<div class="v-leaf-method ' +
          self._escape(pattern.method) +
          '">' +
          self._methodLabelLeaf(pattern.method) +
          '</div>' +
          '<div class="v-leaf-body">' +
          '<div class="v-leaf-url ' +
          (hasUrl ? '' : 'empty-value') +
          '">' +
          (hasUrl ? self._escape(pattern.url) : '(未入力)') +
          '</div>' +
          qpHtml +
          '</div>' +
          extrasHtml +
          '</div>' +
          '</div>'
        );
      }
      function groupHtml(g) {
        var opLabel = g.operator === 'and' ? 'かつ' : 'または';
        var desc = (g.operator === 'and' ? 'すべて' : 'いずれか') + ' (' + g.children.length + ')';
        var childrenHtml = g.children
          .map(function (c) {
            if (c.type === 'group') return groupHtml(c);
            return leafHtml(c);
          })
          .join('');
        return (
          '<div class="v-op-group ' +
          g.operator +
          '">' +
          '<div class="v-op-header">' +
          '<span class="v-op-badge">' +
          opLabel +
          '</span>' +
          '<span class="v-op-desc">' +
          desc +
          '</span>' +
          '</div>' +
          '<div class="v-op-children">' +
          childrenHtml +
          '</div>' +
          '</div>'
        );
      }
      return groupHtml(group);
    },
    generateSummaryHtml: function (group) {
      if (!group || group.children.length === 0) return '';
      var self = this;
      function describeMethod(url, method) {
        var escUrl = '<span class="highlight">' + self._escape(url) + '</span>';
        switch (method) {
        case 'eq':
          return escUrl + ' のページ';
        case 'contains':
          return 'URLに ' + escUrl + ' を含むページ';
        case 'notEq':
          return escUrl + ' 以外のページ';
        case 'notContains':
          return 'URLに ' + escUrl + ' を含まないページ';
        default:
          return escUrl;
        }
      }
      function describeNode(node) {
        if (node.type === 'pattern') {
          if (!node.url || !node.url.trim()) return null;
          var base = describeMethod(node.url, node.method);
          var extras = [];
          if (node.seconds !== null) extras.push(node.seconds + '秒以上滞在');
          if (node.scroll !== null) extras.push(node.scroll + '%以上スクロール');
          node.queryParams
            .filter(function (q) {
              return q.key && q.key.trim();
            })
            .forEach(function (qp) {
              extras.push('クエリ ' + self._escape(qp.key) + '=' + self._escape(qp.value));
            });
          if (extras.length > 0) base += ' (' + extras.join(' / ') + ')';
          return base;
        }
        var parts = node.children.map(describeNode).filter(function (p) {
          return !!p;
        });
        if (parts.length === 0) return null;
        if (parts.length === 1) return parts[0];
        var joiner = node.operator === 'and' ? ' <strong>かつ</strong> ' : ' <strong>または</strong> ';
        return '(' + parts.join(joiner) + ')';
      }
      return describeNode(group) || '';
    },

    // --- グローバルリスナ ---
    _handleDocumentClick: function (e) {
      if (e.target.closest && e.target.closest('.c-select')) return;
      this._closeAllMethodSelects(null);
    },
    _handleDocumentKeydown: function (e) {
      if (e.key === 'Escape' && this.isUrlHelpOpen) {
        this.isUrlHelpOpen = false;
      }
    },

    // --- アクションバー ---
    onQuitClick: function () {
      if (this.conditionInfo && this.conditionInfo.listUrl) {
        location.href = this.conditionInfo.listUrl;
      }
    },
    onBackClick: function () {
      if (this.conditionInfo && this.conditionInfo.editUrl) {
        location.href = this.conditionInfo.editUrl + '?type=edit&message_id=' + this.conditionInfo.messageId;
      }
    },
    onConfirmClick: function () {
      if (this.isLoadingConfirmBtn) return;
      var info = this.conditionInfo || {};
      if (!info.messageId) return;

      // クライアントサイド最低限バリデーション。
      // 結果は「hard errors(フィールド不備) / warning(空条件での保存確認) / そのまま保存」の3分岐。
      var result = this._validateBeforeSubmit();
      this.errors = result.errors;
      if (!result.valid) {
        // ErrorModal は v-html で描画されるため、メッセージ本文は事前にエスケープして <br> で連結する。
        var self = this;
        this.errorMessage = result.messages
          .map(function (m) {
            return self._escape(m);
          })
          .join('<br>');
        this.isError = true;
        // モーダル背面のエラー入力欄まで自動スクロールしておき、
        // ユーザーが閉じた直後にバルーンが視野に入る位置になるようにする。
        this._scrollToFirstError();
        return;
      }
      if (result.needsConfirm) {
        // trigger=auto で schedule が実質空 → 最終確認モーダル経由で保存
        this.isEmptyConditionConfirm = true;
        return;
      }
      this._doSave();
    },

    // 確認ボタンの通常保存処理と「空条件のまま保存」OK 経路の共通実装。
    _doSave: function () {
      var info = this.conditionInfo || {};
      if (!info.messageId) return;
      this.isLoadingConfirmBtn = true;
      var self = this;
      var payload = {
        message_id: info.messageId,
        trigger: this.trigger,
        // trigger=manual は「予約するページ」の設定を持たない運用のため、
        // 内部状態 (this.schedule) に何が入っていても保存時は空 RuleRoot に上書きする。
        // (画面上でも入力欄自体を非表示にしているが、trigger を切り替えて戻した場合の
        //  古い内部状態を保存対象から外すための最終セーフガード。)
        set: this.trigger === 'manual' ? this._convertToRuleRoot(null) : this._convertToRuleRoot(this.schedule),
        unset: this._convertToRuleRoot(this.unschedule),
        // 配信除外リスト (suppress) は API では exclude キーで送信する。
        // バックエンドが condition_exclude カラムに対応するまではサーバ側で無視される。
        exclude: this._convertToRuleRoot(this.suppress),
      };

      axios
        .post('/api/reminder/message/condition/save', payload)
        .then(function (res) {
          self.isLoadingConfirmBtn = false;
          // サーバ側 NG はグローバル showErrors(jQuery) に任せず、画面内モーダルで
          // 代表メッセージ + field_errors の内訳を表示する。
          if (!self.handleResponse(res, false)) {
            self._showServerValidationError(res && res.data ? res.data : null);
            return;
          }
          if (info.confirmUrl) {
            location.href = info.confirmUrl + '?message_id=' + info.messageId;
          }
        })
        .catch(function (err) {
          self.isLoadingConfirmBtn = false;
          self.handleHttpError(err);
        });
    },

    // 空条件確認モーダルのキャンセル(モーダル閉じるのみ、編集画面にとどまる)
    onCancelEmptyConditionConfirm: function () {
      this.isEmptyConditionConfirm = false;
    },
    // 空条件確認モーダルの OK(空のまま保存続行)
    onConfirmEmptyConditionConfirm: function () {
      this.isEmptyConditionConfirm = false;
      this._doSave();
    },

    // サーバ側バリデーション NG をモーダルに展開する。
    // data.field_errors は { <field>: [messages...] } 形式で、現状 trigger / set / unset 粒度。
    // ノード ID を持たないため、フィールド名を人に読めるラベルへ変換して列挙する。
    _showServerValidationError: function (data) {
      var fallback = 'サーバー側でエラーが発生しました。時間をおいて再度お試しください。';
      if (!data) {
        this.errorMessage = this._escape(fallback);
        this.isError = true;
        return;
      }
      var baseMessage = data.message || fallback;
      var fieldErrors = data.field_errors || {};
      var labelMap = {
        trigger: '通知タイミング',
        set: '予約するページ',
        unset: '解除するページ',
        // 送信キーは exclude / DB カラム名 (受信) は condition_exclude。
        // サーバがどちらで返しても同じラベルで表示できるよう両方を登録する。
        exclude: '除外するページ',
        condition_exclude: '除外するページ',
        message_id: 'メッセージID',
      };
      var lines = [this._escape(baseMessage)];
      var fieldKeys = Object.keys(fieldErrors);
      if (fieldKeys.length > 0) {
        lines.push('');
        var self = this;
        fieldKeys.forEach(function (key) {
          var label = labelMap[key] || key;
          var msgs = fieldErrors[key];
          if (!Array.isArray(msgs)) msgs = [String(msgs)];
          msgs.forEach(function (m) {
            lines.push('・' + self._escape(label) + ': ' + self._escape(m));
          });
        });
      }
      this.errorMessage = lines.join('<br>');
      this.isError = true;
    },

    // 指定 nodeId / field のエラーエントリを削除。
    // エラーを解消したフィールドはキー自体を削除する。
    _clearError: function (nodeId, field) {
      var entry = this.errors[nodeId];
      if (!entry) return;
      if (entry[field] == null) return;
      delete entry[field];
      if (Object.keys(entry).length === 0) {
        delete this.errors[nodeId];
      }
    },

    // 確認ボタン押下時の事前検証。
    // 仕様:
    //   - URL 未入力行は『未設定行』として扱い、field 単位の検証もスキップする
    //     (保存 payload 側でも _convertGroupToPages が URL 空 leaf を落とすため整合)
    //   - queryParam は片方だけ入力されている状態を「途中入力」と見なしエラー化する
    //       - key 空 + value 入力 → key エラー
    //       - key 入力 + value 空 → value エラー
    //     両方空の行はその行を保存対象外として扱うためエラーにしない (payload 変換側で key 空はフィルタ)
    //   - trigger=auto かつ schedule が実質空(有効 URL leaf=0)の場合は needsConfirm=true を返し、
    //     呼出し側で警告モーダル経由の最終確認に流す(エラー化せず保存自体は許容する)
    //   - trigger=manual は空条件を正式に許容するため警告も出さない
    //   - duration / scroll は UI 上 +/- ボタンで 5 刻み 5..100 にクランプされるためフロントでは検証せず、
    //     HTML 改変等の異常値はサーバ側 (UrlNodeRule) で検出する
    _validateBeforeSubmit: function () {
      var errors = {};
      var messages = [];
      var self = this;

      var walk = function (group, context) {
        if (!group || !group.children) return;
        group.children.forEach(function (child, idx) {
          if (child.type === 'group') {
            walk(child, context);
            return;
          }
          if (child.type !== 'pattern') return;
          // URL 未入力行は未設定扱いで以降の field 検証ごとスキップ
          if (!child.url || !child.url.trim()) return;

          var label = context + ' #' + (idx + 1);
          var nodeErrors = {};

          (child.queryParams || []).forEach(function (qp, qpIdx) {
            var hasKey = qp.key && qp.key.trim();
            var hasValue = qp.value && qp.value.trim();
            if (!hasKey && hasValue) {
              nodeErrors['query.' + qp.id + '.key'] = 'キーを入力してください';
              messages.push(label + ': クエリ #' + (qpIdx + 1) + ' はキーが未入力です');
            } else if (hasKey && !hasValue) {
              nodeErrors['query.' + qp.id + '.value'] = '値を入力してください';
              messages.push(label + ': クエリ #' + (qpIdx + 1) + ' は値が未入力です');
            }
          });

          if (Object.keys(nodeErrors).length > 0) {
            errors[child.id] = nodeErrors;
          }
        });
      };

      // trigger=manual は「予約するページ」を CMS 側で持たない運用のため、
      // schedule 配下のクエリ入力エラーは検出対象外にする (保存時にも空 RuleRoot 化される)。
      if (this.trigger === 'auto') {
        walk(this.schedule, '予約するページ');
      }
      if (this.unschedule) walk(this.unschedule, '解除するページ');
      if (this.suppress) walk(this.suppress, '除外するページ');

      var hasHardErrors = Object.keys(errors).length > 0;
      var needsConfirm = !hasHardErrors && this.trigger === 'auto' && self._countValidUrls(this.schedule) === 0;

      return {
        valid: !hasHardErrors,
        errors: errors,
        messages: messages,
        needsConfirm: needsConfirm,
      };
    },

    // 指定 group 配下で URL が入っているパターン数をカウント(ネスト再帰対応)
    _countValidUrls: function (group) {
      if (!group) return 0;
      var count = 0;
      var self = this;
      (group.children || []).forEach(function (child) {
        if (child.type === 'pattern' && child.url && child.url.trim()) count++;
        else if (child.type === 'group') count += self._countValidUrls(child);
      });
      return count;
    },

    // テンプレートから nodeId / field のエラーメッセージを参照するヘルパ。
    // ConditionGroupNode 側は inject['condRoot'].errorOf(id, field) で呼ぶ。
    errorOf: function (nodeId, field) {
      var entry = this.errors[nodeId];
      return entry ? entry[field] : null;
    },

    // バリデーションエラー直後、最初のエラー入力欄までスムーススクロールする。
    // ConditionGroupNode が描画する .input-wrap.has-error / .refine-item.has-error を順に探索し、
    // ErrorModal を閉じた直後にバルーンが視野に入る位置で停止させる。
    _scrollToFirstError: function () {
      var self = this;
      this.$nextTick(function () {
        if (!self.$el || typeof self.$el.querySelector !== 'function') return;
        var firstError = self.$el.querySelector('.input-wrap.has-error, .refine-item.has-error, .c-error.is-active');
        if (firstError && typeof firstError.scrollIntoView === 'function') {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },
    onCloseErrorModal: function () {
      this.isError = false;
      this.errorMessage = '';
    },

    // --- v1 ペイロード変換 (内部状態 ↔ RuleRoot) ---

    // 内部 pattern ノードを v1 UrlNode に変換する。URL 未入力は呼出し側でフィルタする前提。
    _convertPatternToUrlNode: function (pattern) {
      var node = {
        type: 'url',
        // URL 条件のフォールバックは 'eq' (等しい)。従来は 'contains' だった。
        // method: pattern.method || 'contains',
        method: pattern.method || 'eq',
        value: pattern.url,
      };
      // ユーザー行動条件 (duration/scroll) は UI 非表示のため保存時にも書き出さない。
      // 内部状態に値が残っていても JSON には含めない。復活させる場合は下記ブロックのコメントを外す。
      /*
            // duration / scroll は >= 5 のときのみ JSON に乗せる(null は未設定扱いで省略)
            if (pattern.seconds !== null && pattern.seconds !== '' && Number(pattern.seconds) >= 5) {
                node.duration = Number(pattern.seconds);
            }
            if (pattern.scroll !== null && pattern.scroll !== '' && Number(pattern.scroll) >= 5) {
                node.scroll = Number(pattern.scroll);
            }
            */
      // querystring.params は key 非空のみを採用、1件以上あるときだけ querystring キーを付ける
      var validQP = (pattern.queryParams || []).filter(function (q) {
        return q.key && q.key.trim();
      });
      if (validQP.length > 0) {
        node.querystring = {
          operator: 'and',
          params: validQP.map(function (q) {
            return {
              key: q.key,
              // QueryString 条件のフォールバックは 'eq' (等しい)。従来は 'contains' だった。
              // method: q.method || 'contains',
              method: q.method || 'eq',
              value: q.value != null ? String(q.value) : '',
            };
          }),
        };
      }
      return node;
    },

    // 内部 group を v1 PagesGroup に変換する。v1 では children に group ノードが来ない想定だが、
    // もし混入していた場合は防御的に子パターンを親へフラット化する。
    _convertGroupToPages: function (group) {
      var self = this;
      var children = [];
      (group.children || []).forEach(function (child) {
        if (child.type === 'pattern') {
          if (!child.url || !child.url.trim()) return;
          children.push(self._convertPatternToUrlNode(child));
        } else if (child.type === 'group') {
          var nested = self._convertGroupToPages(child);
          if (nested.children.length > 0) {
            children = children.concat(nested.children);
          }
        }
      });
      return {
        type: 'group',
        operator: group.operator || 'and',
        children: children,
      };
    },

    // schedule / unschedule の内部 group を RuleRoot に変換する。
    // children が空でも、また group 自体が未作成でも、pages group 構造は必ず保持する
    // (サーバ側 PagesGroupRule は空 children を許容し、SDK 側は常に pages キーの存在を期待する契約)。
    _convertToRuleRoot: function (group) {
      // root operator は or 固定運用のため、group 未作成時の fallback も or で emit する。
      var pages = group ? this._convertGroupToPages(group) : { type: 'group', operator: 'or', children: [] };
      // 仕様: CMS は RuleRoot.operator に PagesGroup.operator と同値を書き込む
      return { operator: pages.operator, conditions: { pages: pages } };
    },

    // v1 UrlNode を内部 pattern に展開する。refine/クエリの展開開閉は値の有無で決定。
    _buildPatternFromUrlNode: function (urlNode) {
      // URL 条件は eq/contains の 2 種のみ対応。レガシー JSON に残っている
      // 'notEq' / 'notContains' は否定を落として 'eq' へコアースする。
      // 従来は urlNode.method をそのまま採用していた:
      //   var pattern = this._createPattern(urlNode.value || '', urlNode.method || 'contains');
      var rawMethod = urlNode.method || 'eq';
      var method = rawMethod === 'notEq' || rawMethod === 'notContains' ? 'eq' : rawMethod;
      var pattern = this._createPattern(urlNode.value || '', method);
      // ユーザー行動条件 (duration/scroll) は UI 非表示のため読み込みをスキップ。
      // pattern.seconds / pattern.scroll は初期値 null のままとし、既存データが持つ値も無視する。
      // 復活させる場合は下記ブロックのコメントを外す。
      /*
            if (urlNode.duration != null) {
                pattern.seconds = Number(urlNode.duration);
            }
            if (urlNode.scroll != null) {
                pattern.scroll = Number(urlNode.scroll);
            }
            if (pattern.seconds !== null || pattern.scroll !== null) {
                pattern.refineOpen = true;
            }
            */
      if (urlNode.querystring && Array.isArray(urlNode.querystring.params) && urlNode.querystring.params.length > 0) {
        var self = this;
        pattern.queryParams = urlNode.querystring.params.map(function (q) {
          // QueryString 読み込みのフォールバックは 'eq'。従来は 'contains' だった。
          // return self._createQueryParam(q.key, q.method || 'contains', q.value);
          return self._createQueryParam(q.key, q.method || 'eq', q.value);
        });
        pattern.queryOpen = true;
      }
      return pattern;
    },

    // RuleRoot を内部 group に戻す。pages が無い/空なら null。
    _buildGroupFromRuleRoot: function (ruleRoot) {
      if (!ruleRoot || typeof ruleRoot !== 'object') return null;
      var pages = ruleRoot.conditions && ruleRoot.conditions.pages;
      if (!pages || !Array.isArray(pages.children) || pages.children.length === 0) return null;
      var self = this;
      var children = pages.children
        .filter(function (c) {
          return c && c.type === 'url';
        })
        .map(function (c) {
          return self._buildPatternFromUrlNode(c);
        });
      if (children.length === 0) return null;
      return this._createGroup(pages.operator || 'and', children);
    },

    // Controller から渡った existingCondition (ReminderMessageCondition レコードの array 化済み) を読み込む。
    _loadExistingCondition: function (existing) {
      if (!existing) return;
      if (existing.trigger_type === 'manual' || existing.trigger_type === 'auto') {
        this.trigger = existing.trigger_type;
      }
      // trigger=manual は「予約するページ」を CMS 側で持たない運用のため、
      // 既存 JSON にレガシーな condition_set が残っていても内部状態に展開しない。
      // (保存時にも空 RuleRoot 化されるため、読み込まないことでデータと UI を一貫させる。)
      if (this.trigger !== 'manual') {
        var scheduleGroup = this._buildGroupFromRuleRoot(existing.condition_set);
        if (scheduleGroup) {
          this.schedule = scheduleGroup;
        }
      }
      var unscheduleGroup = this._buildGroupFromRuleRoot(existing.condition_unset);
      if (unscheduleGroup) {
        this.unschedule = unscheduleGroup;
      }
      // 配信除外リスト (suppress) は API では condition_exclude で受信する。
      // バックエンドが未対応の間は常に undefined となり suppress は null のまま維持される。
      var suppressGroup = this._buildGroupFromRuleRoot(existing.condition_exclude);
      if (suppressGroup) {
        this.suppress = suppressGroup;
      }
      // root operator は or 固定運用 (radio 非表示)。旧データが and で保存されていても or に正規化する。
      if (this.schedule) this.schedule.operator = 'or';
      if (this.unschedule) this.unschedule.operator = 'or';
      if (this.suppress) this.suppress.operator = 'or';
    },
  },
};
</script>

<style>
/* ==========================================================
 *  リマインダー配信条件設定ページ専用スタイル
 *  既存CMSのグローバルCSSと競合しないよう .rm-add-cond 配下に限定。
 *  意図的に非scopedにし、再帰コンポーネント (ConditionGroupNode)
 *  と右プレビューの v-html にもスタイルを適用させる。
 * ========================================================== */
.rm-add-cond {
  --brand: #2bb4d5;
  --brand-hover: #26a3c1;
  --brand-deep: #1e7e97;
  --brand-soft: #e5f4f9;
  --brand-softer: #f0f8fb;

  --bg: #eef2f5;
  --surface: #ffffff;
  --surface-card: #f8fafb;
  --surface-section: #f0f4f6;

  --text: #4a4a4a;
  --text-soft: #757575;
  --text-faint: #9a9a9a;
  --text-heading: #2bb4d5;

  --line: #e0e5e9;
  --line-strong: #c8cfd4;

  --and: #2bb4d5;
  --and-bg: #e5f4f9;
  --and-border: #a7dceb;
  --or: #e78c3f;
  --or-bg: #fdf1e5;
  --or-border: #f5cda3;

  /* 解除するページ (unschedule) のカラーパレット。従来は赤系だったが、
     警告ではなく「予約解除」の意味合いに合わせて黄色系 (amber) に変更。
     従来の赤系:
       --unschedule: #d1556e;
       --unschedule-bg: #fbeff2;
       --unschedule-border: #f0c4cf;
  */
  --unschedule: #b7791f;
  --unschedule-bg: #fef6d7;
  --unschedule-border: #f2d78e;

  /* --danger は削除/エラー系の色として赤を維持する (unschedule とは意味的に別) */
  --danger: #d1556e;

  /* 配信除外リスト (suppress) のカラーパレット。従来 exclude で使っていた赤系を流用する。
     一度該当したら二度と予約されない永続除外の意味を強い赤で表現する。 */
  --suppress: #d1556e;
  --suppress-bg: #fbeff2;
  --suppress-border: #f0c4cf;
}

.rm-add-cond .rm-add-cond__content {
  width: 100%;
  max-width: 1400px;
  padding: 0;
}

.rm-add-cond .split-container {
  display: grid;
  grid-template-columns: 1fr 420px;
  gap: 16px;
  align-items: start;
  margin: 20px 0 60px;
}

.rm-add-cond .panel {
  background: var(--surface-section);
  border-radius: 4px;
  display: flex;
  flex-direction: column;
}

.rm-add-cond .panel-editor {
  display: flex;
  flex-direction: column;
}
.rm-add-cond .panel-body {
  padding: 20px 24px;
}
/* 左カラム内でセクション毎の panel を縦に積むためのラッパ。
   メッセージ内容編集ページと同じく、各ブロックは独立した panel カードとして表示され、
   ブロック間は 3px の細い隙間で区切る (罫線の代わり)。 */
.rm-add-cond .editor-stack {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.rm-add-cond .section-question {
  margin-bottom: 14px;
}
.rm-add-cond .section-question h2 {
  font-size: 16px;
  font-weight: 700;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
}
.rm-add-cond .section-question .section-desc {
  font-size: 12px;
  color: var(--text-soft);
}
.rm-add-cond .section-question .section-desc-list {
  margin: 0;
  padding-left: 18px;
  line-height: 1.6;
  list-style: disc;
}
.rm-add-cond .section-question .optional-tag {
  font-size: 11px;
  color: var(--text-soft);
  font-weight: 500;
  margin-left: 4px;
}
/* サブセクション見出し (親パネル内でネストされた小見出し用。h2 より小さく控えめに表示)
   サブ見出し間を明確に区切るため、上側に余白を確保する */
.rm-add-cond .section-question--sub {
  margin-top: 24px;
  margin-bottom: 10px;
}
.rm-add-cond .section-question--sub h3 {
  font-size: 13px;
  font-weight: 700;
  color: var(--text);
  margin: 0 0 4px;
}

/* トリガー選択カード */
.rm-add-cond .trigger-selector {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
}
.rm-add-cond .trigger-card {
  background: white;
  border: 2px solid var(--line);
  border-radius: 4px;
  padding: 20px 24px;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.rm-add-cond .trigger-card:hover {
  border-color: var(--line-strong);
  background: var(--surface-card);
}
.rm-add-cond .trigger-card.selected {
  border-color: var(--brand);
  background: white;
  box-shadow: 0 0 0 1px var(--brand);
}
.rm-add-cond .trigger-card-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
}
.rm-add-cond .trigger-card-desc {
  font-size: 12px;
  color: var(--text-soft);
  line-height: 1.5;
}

.rm-add-cond .viewer-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: sticky;
  top: 72px;
}

.rm-add-cond .panel-templates {
  background: white;
  border: 1px solid var(--line);
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
}
.rm-add-cond .templates-head {
  padding: 14px 18px;
  border-bottom: 1px solid var(--line);
  background: var(--surface-section);
}
.rm-add-cond .templates-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
}
.rm-add-cond .templates-desc {
  font-size: 12px;
  color: var(--text-soft);
  line-height: 1.6;
  margin-top: 4px;
}
.rm-add-cond .templates-body {
  padding: 14px 18px;
}
.rm-add-cond .templates-content-wrap {
  position: relative;
  overflow: hidden;
  max-height: 100px;
  transition: max-height 0.3s ease;
}
.rm-add-cond .panel-templates.open .templates-content-wrap {
  max-height: 2000px;
}
.rm-add-cond .panel-templates:not(.open) .templates-content-wrap::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, white 90%);
  pointer-events: none;
  z-index: 1;
}
.rm-add-cond .panel-templates:not(.open) .template-card {
  pointer-events: none;
}
.rm-add-cond .templates-toggle-container {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 4px;
  display: flex;
  justify-content: center;
  z-index: 2;
}
.rm-add-cond .templates-toggle-btn {
  background: white;
  border: 1px solid var(--brand);
  color: var(--brand);
  padding: 6px 20px;
  font-size: 12px;
  border-radius: 999px;
  cursor: pointer;
  font-family: inherit;
  font-weight: 500;
  transition: all 0.15s;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.08);
}
.rm-add-cond .templates-toggle-btn:hover {
  background: var(--brand-softer);
}

.rm-add-cond .templates-grid {
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: 1fr;
  gap: 8px;
}
.rm-add-cond .template-card {
  background: white;
  border: 1px solid var(--line);
  border-radius: 3px;
  padding: 10px 12px;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.rm-add-cond .template-card:hover {
  border-color: var(--line-strong);
  background: var(--surface-card);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
}
.rm-add-cond .template-name {
  font-size: 12px;
  font-weight: 700;
  color: var(--text);
  line-height: 1.5;
}
.rm-add-cond .template-desc {
  font-size: 11px;
  color: var(--text-soft);
  line-height: 1.6;
}

/* カテゴリブロック */
.rm-add-cond .category-block {
  background: white;
  border: 1px solid var(--line);
  border-radius: 4px;
  margin-bottom: 12px;
  overflow: hidden;
}
.rm-add-cond .category-block-head {
  padding: 14px 18px 10px;
  border-bottom: 1px solid var(--line);
}
.rm-add-cond .cat-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
}
.rm-add-cond .cat-desc {
  font-size: 12px;
  color: var(--text-soft);
}
.rm-add-cond .category-block-body {
  padding: 18px;
}

/* URLヘルプリンク */
.rm-add-cond .url-help-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 6px;
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: 12px;
  color: var(--brand);
  cursor: pointer;
  text-decoration: none;
}
.rm-add-cond .url-help-link:hover {
  text-decoration: underline;
}
.rm-add-cond .url-help-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 1px solid var(--brand);
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
}

/* manual トリガー選択時に「予約するページ」入力欄の代わりに表示する説明ブロック。 */
.rm-add-cond .manual-trigger-notice {
  margin-top: 12px;
  padding: 16px 18px;
  background: var(--brand-softer);
  border: 1px solid var(--line);
  border-radius: 6px;
  color: var(--text);
  font-size: 13px;
  line-height: 1.7;
}
.rm-add-cond .manual-trigger-notice__p {
  margin: 0;
}
.rm-add-cond .manual-trigger-notice__p + .manual-trigger-notice__p {
  margin-top: 10px;
}

/* ルート演算子ラジオ */
.rm-add-cond .operator-radio-row {
  display: flex;
  gap: 24px;
  margin-top: 12px;
}
.rm-add-cond .operator-radio {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  color: var(--text);
  user-select: none;
}
.rm-add-cond .operator-radio input[type='radio'] {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.rm-add-cond .operator-radio-dot {
  width: 16px;
  height: 16px;
  border: 2px solid var(--line-strong);
  border-radius: 50%;
  flex-shrink: 0;
  position: relative;
  background: white;
  transition: border-color 0.12s;
}
.rm-add-cond .operator-radio.selected .operator-radio-dot {
  border-color: var(--brand);
}
.rm-add-cond .operator-radio.selected .operator-radio-dot::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 8px;
  height: 8px;
  background: var(--brand);
  border-radius: 50%;
}
.rm-add-cond .operator-radio.selected .operator-radio-label {
  color: var(--text);
  font-weight: 600;
}
.rm-add-cond .operator-radio-row.unschedule .operator-radio.selected .operator-radio-dot {
  border-color: var(--unschedule);
}
.rm-add-cond .operator-radio-row.unschedule .operator-radio.selected .operator-radio-dot::after {
  background: var(--unschedule);
}

/* グループラップ */
.rm-add-cond .group-wrap {
  border: 2px solid var(--or);
  border-radius: 4px;
  margin: 0 0 20px 0;
  background: linear-gradient(180deg, var(--or-bg), white 30%);
  overflow: hidden;
}
.rm-add-cond .group-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--or-border);
  background: rgba(255, 255, 255, 0.6);
}
.rm-add-cond .group-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px 6px 8px;
  background: white;
  border: 1px dashed var(--line-strong);
  color: var(--text);
  font-size: 13px;
  font-weight: 400;
  border-radius: 3px;
  letter-spacing: 0.02em;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.12s;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06), 0 2px 4px rgba(15, 23, 42, 0.04);
}
.rm-add-cond .group-badge .op-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-size: 9px;
  font-weight: 700;
  color: white;
}
.rm-add-cond .group-badge.and {
  border-color: var(--brand);
}
.rm-add-cond .group-badge.and .op-icon {
  background: var(--brand);
}
.rm-add-cond .group-badge.or {
  border-color: var(--or);
}
.rm-add-cond .group-badge.or .op-icon {
  background: var(--or);
}
.rm-add-cond .group-badge.and:hover {
  background: var(--brand-softer);
}
.rm-add-cond .group-badge.or:hover {
  background: var(--or-bg);
}

.rm-add-cond .group-title {
  font-size: 12px;
  color: var(--text-soft);
  flex: 1;
}
.rm-add-cond .group-title strong {
  color: var(--text);
  font-weight: 600;
}
.rm-add-cond .group-title__count {
  margin-left: 6px;
  font-size: 11px;
  color: var(--text-faint);
}
.rm-add-cond .group-body {
  padding: 10px;
  background: white;
}

/* 条件アイテム */
.rm-add-cond .condition-item {
  border: 1px solid var(--line);
  border-radius: 4px;
  padding: 14px;
  margin-bottom: 20px;
  background: white;
  transition: all 0.15s;
  position: relative;
}
.rm-add-cond .condition-item:hover {
  border-color: var(--line-strong);
}
.rm-add-cond .condition-item.highlighted {
  border-color: #999;
  box-shadow: 0 0 0 2px rgba(153, 153, 153, 0.2);
}
.rm-add-cond .cond-row {
  display: grid;
  /* 削除ボタンは .condition-item 直下に移動して absolute 配置するため、
       cond-row のグリッドは URL 入力 (1fr) と method セレクト (90px) の 2 列構成に変更。
       従来: 'grid-template-columns: 1fr 162px 28px;' */
  grid-template-columns: 1fr 90px;
  gap: 10px;
  align-items: center;
}
/* 削除ボタン (.condition-item 直下の .icon-btn) はカード右上の角に半分だけ重ねる。
   通常時は非表示、カードにマウスホバー/フォーカス内包時のみ表示する。
   丸型ボタンにするため border-radius を 50% で上書きする。 */
.rm-add-cond .condition-item > .icon-btn {
  position: absolute;
  top: -14px;
  right: -14px;
  border-radius: 50%;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s;
  z-index: 1;
}
.rm-add-cond .condition-item:hover > .icon-btn,
.rm-add-cond .condition-item:focus-within > .icon-btn {
  opacity: 1;
  pointer-events: auto;
}
/* 兄弟ノード間の区切り ("または" / "かつ")。中央に淡いグレーで文字のみ表示 */
.rm-add-cond .condition-separator {
  text-align: center;
  color: #bbb;
  font-size: 11px;
  letter-spacing: 0.05em;
  /* .condition-item の margin-bottom (20px) の隙間に食い込ませて、
       上下の余白バランスを整える */
  margin: -6px 0 14px 0;
}

/* 入力 */
.rm-add-cond .input-field {
  border: 1px solid var(--line-strong);
  background: white;
  border-radius: 3px;
  padding: 8px 12px;
  font-family: inherit;
  font-size: 13px;
  color: var(--text);
  width: 100%;
  transition: border-color 0.12s;
}
.rm-add-cond .input-field::placeholder {
  color: var(--text-faint);
  font-weight: 400;
}
.rm-add-cond .input-field:focus {
  outline: none;
  border-color: #999;
}

.rm-add-cond .select-wrap {
  position: relative;
}
.rm-add-cond .select-field {
  border: 1px solid var(--line-strong);
  background: white;
  border-radius: 3px;
  padding: 8px 28px 8px 12px;
  font-family: inherit;
  font-size: 13px;
  color: var(--text);
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
}
.rm-add-cond .select-field:focus {
  outline: none;
  border-color: #999;
}
.rm-add-cond .select-wrap::after {
  content: '▼';
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  font-size: 9px;
  pointer-events: none;
}

/* アイコンボタン */
.rm-add-cond .icon-btn {
  width: 28px;
  height: 28px;
  border: 1px solid var(--line);
  background: white;
  border-radius: 3px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-soft);
  font-size: 14px;
  transition: all 0.12s;
  font-family: inherit;
}
.rm-add-cond .icon-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}
.rm-add-cond .icon-btn:hover:not(:disabled) {
  background: var(--surface-card);
  color: var(--text);
}
/* icon-btn 内の × を sprite の close-thin で描画する。currentColor を使うので
   icon-btn 側の color (hover 含む) がそのまま反映される。 */
.rm-add-cond .icon-btn .icon-x {
  width: 12px;
  height: 12px;
  fill: currentColor;
  pointer-events: none;
}
.rm-add-cond .icon-btn.danger:hover:not(:disabled) {
  background: var(--unschedule-bg);
  color: var(--danger);
  border-color: var(--unschedule-border);
}

/* セレクトは共通コンポーネント SelectText を利用するが、
   本画面ではグレー系 UI に統一したいので hover / focus の primary 色を上書きする。 */
.rm-add-cond .c-select__selected:hover {
  border-color: #999;
}
.rm-add-cond .c-select__item:focus {
  border-color: #999;
}
/* SelectText 既定スタイル(font-size:16px/padding:11px)は本画面の .input-field より背が高い。
   URL 行で隣接するテキスト入力と高さを揃えるため、input-field と同等の余白・文字サイズに縮める。 */
.rm-add-cond .cond-row .c-select__selected {
  height: 38px;
  padding: 9px 28px 8px 12px;
  font-size: 13px;
  border-radius: 3px;
  border-color: var(--line-strong);
  background-position: right 10px center;
  background-size: 8px auto;
}

/* メソッド例示 */
.rm-add-cond .method-example {
  margin-top: 8px;
  padding: 10px 12px;
  background: var(--surface-section);
  border-radius: 3px;
  font-size: 11px;
  color: var(--text-soft);
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  align-items: center;
  line-height: 1.6;
}
.rm-add-cond .method-example .match {
  color: var(--brand-deep);
  font-weight: 600;
  background: var(--brand-softer);
  padding: 1px 6px;
  border-radius: 2px;
  border: 1px solid #c5e4ec;
}
.rm-add-cond .method-example .nomatch {
  color: var(--text-faint);
}

/* リファインメント */
.rm-add-cond .refinement-row {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px dashed var(--line);
  font-size: 12px;
  color: var(--text-soft);
}
.rm-add-cond .refinement-toggle-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
/* 「クエリストリングで絞り込む」「ユーザー行動の条件でさらに絞り込む」用トグル。
   クエリストリング追加ボタン (.query-add-btn) と同じ灰色系の pill に揃える。 */
.rm-add-cond .refinement-toggle {
  color: #999;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  padding: 3px 10px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.15s;
  /* clear-btn と同じ高さに固定し、表示切替時のレイアウトシフトを防ぐ */
  min-height: 24px;
}
.rm-add-cond .refinement-toggle .caret {
  font-size: 9px;
  transition: transform 0.12s;
}
/* + 文字はフォントの ascent ぶん視覚的に下がって見えるため、軽くオフセットして中心に揃える。 */
.rm-add-cond .refinement-toggle > span:first-child {
  margin-top: -2px;
}
.rm-add-cond .refinement-toggle:hover {
  background: #f5f5f5;
  border-color: #999;
}

.rm-add-cond .clear-btn {
  background: white;
  border: 1px solid var(--line-strong);
  border-radius: 3px;
  padding: 2px 10px;
  font-size: 11px;
  color: var(--text-soft);
  cursor: pointer;
  margin-left: 4px;
  font-family: inherit;
  /* refinement-toggle と同じ高さに揃え、表示切替時のレイアウトシフトを防ぐ */
  min-height: 24px;
  box-sizing: border-box;
}
.rm-add-cond .clear-btn:hover {
  background: var(--surface-card);
  color: var(--text);
}

.rm-add-cond .optional-badge {
  font-size: 10px;
  color: var(--text-faint);
  padding: 1px 6px;
  background: var(--surface-card);
  border: 1px solid var(--line);
  border-radius: 2px;
  margin-left: 4px;
}

/* トグルを開いたときに条件入力部分が灰色枠でグルーピングされて見えるよう、
   クエリストリングの .query-params-list と同じ背景・余白に揃える。
   ノート("以下のすべてを満たす")を上に置くため flex は縦方向。 */
.rm-add-cond .refinement-controls {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
  padding: 10px 12px;
  background: var(--surface-section);
  border-radius: 4px;
  width: 100%;
  box-sizing: border-box;
}
.rm-add-cond .refine-items {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.rm-add-cond .refine-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}
.rm-add-cond .refine-label {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--text);
}
.rm-add-cond .refine-label .icon {
  color: var(--text);
  font-size: 13px;
}
.rm-add-cond .plus-minus {
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--line-strong);
  border-radius: 3px;
  overflow: hidden;
  background: white;
}
.rm-add-cond .pm-btn {
  background: var(--surface-card);
  border: none;
  width: 22px;
  height: 24px;
  font-family: inherit;
  color: var(--text);
  cursor: pointer;
  font-size: 12px;
  /* − / + 文字がフォントの ascent ぶん下にずれて見えるため微調整。 */
  margin-top: -2px;
}
.rm-add-cond .pm-btn:hover {
  background: #f5f5f5;
  color: #333;
}
.rm-add-cond .pm-input {
  width: 44px;
  height: 24px;
  border: none;
  border-left: 1px solid var(--line);
  border-right: 1px solid var(--line);
  font-family: inherit;
  font-size: 12px;
  text-align: center;
  color: var(--text);
  background: white;
}
.rm-add-cond .pm-input:focus {
  outline: none;
}
.rm-add-cond .refine-unit {
  font-size: 12px;
  color: var(--text-soft);
  margin-left: 2px;
}

/* クエリパラメータ */
.rm-add-cond .query-section {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px dashed var(--line);
}
.rm-add-cond .query-toggle-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--text-soft);
  flex-wrap: wrap;
}
.rm-add-cond .query-params-list {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 12px;
  background: var(--surface-section);
  border-radius: 4px;
}
.rm-add-cond .query-params-note {
  font-size: 11px;
  color: var(--text-soft);
  font-weight: 500;
}
.rm-add-cond .query-param-row {
  display: grid;
  grid-template-columns: 1fr 110px 1fr 28px;
  gap: 6px;
  align-items: center;
}
/* input と native select はブラウザ既定の line-height が異なり高さがずれることがあるので、
   line-height と height を明示してキー/値/メソッドを同じ高さに揃える。 */
.rm-add-cond .query-param-row .input-field,
.rm-add-cond .query-param-row .select-field {
  height: 30px;
  line-height: 16px;
  box-sizing: border-box;
}
.rm-add-cond .query-param-row .input-field {
  padding: 6px 10px;
  font-size: 12px;
  background: white;
}
.rm-add-cond .query-param-row .select-field {
  padding: 6px 24px 6px 10px;
  font-size: 12px;
  background: white;
}
.rm-add-cond .query-param-row .select-wrap {
  background: white;
  border-radius: 3px;
}
/* プレビュー内「メッセージの削除」ボタン(preview-action-btn--delete)と同じ小さなグレー角丸デザインに合わせる。
   親の .query-params-list は flex column + align-items:stretch なので align-self で文字幅に揃える。 */
.rm-add-cond .query-add-btn {
  margin-top: 8px;
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: inherit;
  font-size: 10px;
  font-weight: 500;
  color: #999;
  background: #fff;
  border: 1px solid #ccc;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
.rm-add-cond .query-add-btn:hover {
  background: #f5f5f5;
  border-color: #999;
}

/* 追加ボタン群 */
.rm-add-cond .add-row {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}
/* 「さらに条件を追加する」ボタン。クエリストリング追加ボタンと同じ灰色系に統一する。
   dashed の枠だけ残してプロンプト感を維持しつつ色は全てグレー寄せ。 */
.rm-add-cond .add-btn-main {
  flex: 1;
  padding: 12px;
  background: white;
  border: 1px dashed #ccc;
  border-radius: 3px;
  color: #999;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.12s;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06), 0 2px 4px rgba(15, 23, 42, 0.04);
}
.rm-add-cond .add-btn-main:hover:not(:disabled) {
  background: #f5f5f5;
  border-color: #999;
}
.rm-add-cond .add-btn-main:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  border-color: var(--line-strong);
  color: var(--text-faint);
}
/* + アイコンは差し込みテキスト追加ボタン (c-icon-circle) と同じ外枠 20px / SVG 12px の幾何で中心配置する。
   テキスト "+" 文字だと glyph のベアリングでわずかに中心ずれするため SVG スプライトに統一。 */
.rm-add-cond .add-btn-main .circle-plus {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #999;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.rm-add-cond .add-btn-main .circle-plus svg {
  width: 12px;
  height: 12px;
  fill: #fff;
}
.rm-add-cond .add-btn-group {
  padding: 12px 16px;
  background: white;
  border: 1px dashed var(--or);
  border-radius: 3px;
  color: var(--or);
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.12s;
  white-space: nowrap;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06), 0 2px 4px rgba(15, 23, 42, 0.04);
}
.rm-add-cond .add-btn-group:hover:not(:disabled) {
  background: var(--or-bg);
}
.rm-add-cond .add-btn-group .circle-plus {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--or);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.rm-add-cond .add-btn-group .circle-plus svg {
  width: 12px;
  height: 12px;
  fill: #fff;
}
.rm-add-cond .add-row.nested {
  margin-top: 8px;
}
.rm-add-cond .add-row.nested .add-btn-main,
.rm-add-cond .add-row.nested .add-btn-group {
  padding: 8px 12px;
  font-size: 12px;
}

/* 予約セクション (schedule)。
   条件プレビューの schedule 表示 (--and-* / --brand-*) と同じ青系トーンで
   category-block のデフォルト (白背景 / グレー罫線) を上書きする。
   構造は unschedule-section-block と対称。 */
.rm-add-cond .schedule-section-block {
  background: #f5fbfd;
  border: 1px solid var(--and-border);
  border-radius: 4px;
  margin-bottom: 12px;
  overflow: hidden;
}
.rm-add-cond .schedule-section-block .category-block-head {
  background: var(--and-bg);
  border-bottom-color: var(--and-border);
}
.rm-add-cond .schedule-section-block .cat-title {
  color: var(--brand-deep);
}

/* 除外セクション */
.rm-add-cond .unschedule-section-block {
  /* 従来は薄ピンク #fdf5f7。unschedule を黄色系にしたので薄いクリーム系に変更 */
  background: #fefaea;
  border: 1px solid var(--unschedule-border);
  border-radius: 4px;
  margin-top: 12px;
  overflow: hidden;
}
.rm-add-cond .unschedule-section-block .category-block-head {
  /* 従来は薄ピンク #fbeff2。unschedule を黄色系にしたので薄い黄色系に変更 */
  background: #fdf3d0;
  border-bottom-color: var(--unschedule-border);
}
.rm-add-cond .unschedule-section-block .cat-title {
  color: var(--unschedule);
}

/* 配信除外リストセクション (suppress)。
   永続除外の意味を強調するため従来 exclude の赤系を使う。 */
.rm-add-cond .suppress-section-block {
  background: #fdf5f7;
  border: 1px solid var(--suppress-border);
  border-radius: 4px;
  margin-top: 12px;
  overflow: hidden;
}
.rm-add-cond .suppress-section-block .category-block-head {
  background: var(--suppress-bg);
  border-bottom-color: var(--suppress-border);
}
.rm-add-cond .suppress-section-block .cat-title {
  color: var(--suppress);
}

.rm-add-cond .unschedule-hint {
  background: white;
  border: 1px solid var(--unschedule-border);
  border-radius: 3px;
  padding: 10px 12px;
  font-size: 11px;
  color: var(--text-soft);
  margin-bottom: 14px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  line-height: 1.5;
}
.rm-add-cond .unschedule-hint .hint-icon {
  width: 16px;
  height: 16px;
  background: var(--unschedule);
  color: white;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 1px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
}
.rm-add-cond .unschedule-hint strong {
  color: var(--unschedule);
  display: block;
  margin-bottom: 2px;
}
.rm-add-cond .unschedule-empty {
  padding: 20px;
  text-align: center;
  background: white;
  border: 1px dashed var(--unschedule-border);
  border-radius: 3px;
  color: var(--text-soft);
  font-size: 12px;
  margin-bottom: 10px;
}
.rm-add-cond .unschedule-empty-icon {
  font-size: 24px;
  color: var(--unschedule-border);
  margin-bottom: 6px;
}
/* schedule=null (予約するページ未設定) の empty state。unschedule 側 .unschedule-empty と対称。 */
.rm-add-cond .schedule-empty {
  padding: 20px;
  text-align: center;
  background: white;
  border: 1px dashed var(--and-border);
  border-radius: 3px;
  color: var(--text-soft);
  font-size: 12px;
  margin-bottom: 10px;
}
.rm-add-cond .schedule-empty-icon {
  font-size: 24px;
  color: var(--and-border);
  margin-bottom: 6px;
}
/* suppress=null (配信除外未設定) の empty state。unschedule 側 .unschedule-empty と対称。 */
.rm-add-cond .suppress-empty {
  padding: 20px;
  text-align: center;
  background: white;
  border: 1px dashed var(--suppress-border);
  border-radius: 3px;
  color: var(--text-soft);
  font-size: 12px;
  margin-bottom: 10px;
}
.rm-add-cond .suppress-empty-icon {
  font-size: 24px;
  color: var(--suppress-border);
  margin-bottom: 6px;
}
.rm-add-cond .add-btn-unschedule {
  width: 100%;
  padding: 12px;
  background: white;
  border: 1px dashed #ccc;
  border-radius: 3px;
  color: #999;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.12s;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06), 0 2px 4px rgba(15, 23, 42, 0.04);
}
.rm-add-cond .add-btn-unschedule:hover {
  background: #f5f5f5;
  border-color: #999;
}
.rm-add-cond .add-btn-unschedule .circle-plus {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #999;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.rm-add-cond .add-btn-unschedule .circle-plus svg {
  width: 12px;
  height: 12px;
  fill: #fff;
}
/* .add-btn-schedule は .add-btn-unschedule と同じ落ち着いた中立色 (グレー) を使う。
   ボタン内アイコンや文字は theme を主張せず、追加後の本編コンポーネントで schedule テーマ色が出る想定。 */
.rm-add-cond .add-btn-schedule {
  width: 100%;
  padding: 12px;
  background: white;
  border: 1px dashed #ccc;
  border-radius: 3px;
  color: #999;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.12s;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06), 0 2px 4px rgba(15, 23, 42, 0.04);
}
.rm-add-cond .add-btn-schedule:hover {
  background: #f5f5f5;
  border-color: #999;
}
.rm-add-cond .add-btn-schedule .circle-plus {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #999;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.rm-add-cond .add-btn-schedule .circle-plus svg {
  width: 12px;
  height: 12px;
  fill: #fff;
}
/* .add-btn-suppress は .add-btn-unschedule / .add-btn-schedule と同じ落ち着いた中立色 (グレー) を使う。
   ボタン内アイコンや文字は theme を主張せず、追加後の本編コンポーネントで suppress テーマ色が出る想定。 */
.rm-add-cond .add-btn-suppress {
  width: 100%;
  padding: 12px;
  background: white;
  border: 1px dashed #ccc;
  border-radius: 3px;
  color: #999;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.12s;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06), 0 2px 4px rgba(15, 23, 42, 0.04);
}
.rm-add-cond .add-btn-suppress:hover {
  background: #f5f5f5;
  border-color: #999;
}
.rm-add-cond .add-btn-suppress .circle-plus {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #999;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.rm-add-cond .add-btn-suppress .circle-plus svg {
  width: 12px;
  height: 12px;
  fill: #fff;
}

/* 右パネル: ビューア */
.rm-add-cond .panel-viewer {
  background: white;
  border: 1px solid var(--line);
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.rm-add-cond .viewer-head {
  padding: 14px 18px;
  border-bottom: 1px solid var(--line);
  background: var(--surface-section);
  flex-shrink: 0;
}
.rm-add-cond .viewer-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 8px;
}
.rm-add-cond .viewer-title .eye-icon {
  color: var(--brand);
  font-size: 14px;
}
.rm-add-cond .viewer-body {
  padding: 16px 18px;
}
.rm-add-cond .v-full-empty {
  padding: 40px 20px;
  text-align: center;
  color: var(--text-faint);
}
.rm-add-cond .v-full-empty-icon {
  font-size: 40px;
  color: var(--line-strong);
  margin-bottom: 12px;
  line-height: 1;
}
.rm-add-cond .v-full-empty-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-soft);
  margin-bottom: 6px;
}
.rm-add-cond .v-full-empty-desc {
  font-size: 11px;
  color: var(--text-faint);
  line-height: 1.6;
}
.rm-add-cond .v-summary-block {
  margin-bottom: 14px;
}
.rm-add-cond .v-summary-label {
  font-size: 11px;
  font-weight: 700;
  color: var(--text-soft);
  letter-spacing: 0.05em;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--line);
}
.rm-add-cond .v-summary-text {
  font-size: 12px;
  color: var(--text);
  line-height: 1.7;
}
.rm-add-cond .v-summary-text .highlight {
  background: var(--brand-softer);
  color: var(--brand-deep);
  padding: 1px 6px;
  border-radius: 2px;
  font-weight: 600;
  border: 1px solid #c5e4ec;
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  font-size: 11px;
}
.rm-add-cond .v-summary-text strong {
  color: var(--text);
  font-weight: 700;
}
.rm-add-cond .v-tree-label {
  font-size: 11px;
  font-weight: 700;
  color: var(--text-soft);
  letter-spacing: 0.05em;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--line);
}
.rm-add-cond .v-section {
  border: 1px solid var(--line);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 10px;
  background: white;
}
.rm-add-cond .v-section.schedule {
  border-color: var(--and-border);
}
.rm-add-cond .v-section.unschedule {
  border-color: var(--unschedule-border);
}
.rm-add-cond .v-section.suppress {
  border-color: var(--suppress-border);
}
.rm-add-cond .v-section-head {
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid var(--line);
}
.rm-add-cond .v-section.schedule .v-section-head {
  background: var(--and-bg);
  color: var(--brand-deep);
  border-bottom-color: var(--and-border);
}
.rm-add-cond .v-section.unschedule .v-section-head {
  background: var(--unschedule-bg);
  color: var(--unschedule);
  border-bottom-color: var(--unschedule-border);
}
.rm-add-cond .v-section.suppress .v-section-head {
  background: var(--suppress-bg);
  color: var(--suppress);
  border-bottom-color: var(--suppress-border);
}
.rm-add-cond .v-section-body {
  padding: 10px;
}
.rm-add-cond .v-section-empty {
  padding: 14px;
  text-align: center;
  color: var(--text-faint);
  font-size: 11px;
  font-style: italic;
}

/* ビューア: opグループ / リーフ */
.rm-add-cond .v-op-group {
  border: 1px solid var(--line-strong);
  background: white;
  border-radius: 3px;
  margin: 4px 0;
}
.rm-add-cond .v-op-header {
  padding: 5px 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  display: flex;
  align-items: center;
  gap: 6px;
  border-bottom: 1px solid var(--line-strong);
  background: white;
  color: var(--text);
}
.rm-add-cond .v-op-badge {
  padding: 1px 6px;
  background: white;
  border: 1px solid var(--line-strong);
  font-size: 9px;
  letter-spacing: 0.08em;
  border-radius: 2px;
  font-weight: 700;
  color: var(--text);
}
.rm-add-cond .v-op-group.and > .v-op-header > .v-op-badge {
  border-color: var(--brand);
  color: var(--brand);
}
.rm-add-cond .v-op-group.or > .v-op-header > .v-op-badge {
  border-color: var(--or);
  color: var(--or);
}
.rm-add-cond .v-op-desc {
  font-weight: 400;
  font-size: 9px;
  color: var(--text-soft);
  margin-left: auto;
}
.rm-add-cond .v-op-children {
  padding: 7px;
  position: relative;
}
.rm-add-cond .v-op-children::before {
  content: '';
  position: absolute;
  left: 14px;
  top: 7px;
  bottom: 7px;
  width: 1px;
  background: repeating-linear-gradient(0deg, var(--text), var(--text) 3px, transparent 3px, transparent 6px);
}
.rm-add-cond .v-op-children > .v-node {
  position: relative;
  padding-left: 18px;
  margin-bottom: 4px;
}
.rm-add-cond .v-op-children > .v-node:last-child {
  margin-bottom: 0;
}
.rm-add-cond .v-op-children > .v-node::before {
  content: '';
  position: absolute;
  left: 6px;
  top: 12px;
  width: 10px;
  height: 1px;
  background: var(--text);
}
.rm-add-cond .v-op-children > .v-op-group {
  margin-left: 18px;
  position: relative;
}
.rm-add-cond .v-op-children > .v-op-group::before {
  content: '';
  position: absolute;
  left: -12px;
  top: 12px;
  width: 12px;
  height: 1px;
  background: var(--text);
}

.rm-add-cond .v-leaf {
  display: inline-flex;
  align-items: stretch;
  border: 1px solid var(--line);
  background: white;
  border-radius: 3px;
  overflow: hidden;
  max-width: 100%;
  min-height: 28px;
  cursor: pointer;
  transition: all 0.12s;
}
.rm-add-cond .v-leaf:hover {
  border-color: #999;
}
.rm-add-cond .v-leaf.highlighted {
  border-color: #999;
  box-shadow: 0 0 0 2px rgba(153, 153, 153, 0.2);
  background: #f5f5f5;
}
.rm-add-cond .v-leaf-method {
  padding: 4px 8px;
  font-size: 10px;
  display: flex;
  align-items: center;
  border-right: 1px solid var(--line);
  white-space: nowrap;
  background: white;
  color: var(--text);
}
.rm-add-cond .v-leaf-body {
  padding: 4px 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  min-width: 0;
}
.rm-add-cond .v-leaf-url {
  font-size: 11px;
  color: var(--text);
  font-weight: 500;
  word-break: break-all;
  line-height: 1.3;
  font-family: 'SF Mono', Menlo, Consolas, monospace;
}
.rm-add-cond .v-leaf-url.empty-value {
  color: var(--text-faint);
  font-style: italic;
}
.rm-add-cond .v-leaf-qps {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  margin-top: 3px;
}
.rm-add-cond .v-leaf-qp {
  font-size: 10px;
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  background: var(--surface-card);
  border: 1px solid var(--line);
  border-radius: 2px;
  padding: 1px 5px;
  color: var(--text-soft);
}
.rm-add-cond .v-leaf-qp__op {
  color: var(--text-faint);
  margin: 0 2px;
}
.rm-add-cond .v-leaf-extras {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0 6px;
  border-left: 1px dashed var(--line);
  background: var(--surface-card);
}
.rm-add-cond .v-leaf-extras .extra {
  font-size: 10px;
  color: var(--text-soft);
  padding: 2px 4px;
  display: flex;
  align-items: center;
  gap: 2px;
  font-weight: 600;
}

/* URL help モーダル */
.rm-add-cond.url-help-modal {
  display: none;
  position: fixed;
  inset: 0;
  z-index: 1000;
}
.rm-add-cond.url-help-modal.is-open {
  display: flex;
  align-items: center;
  justify-content: center;
}
.rm-add-cond .url-help-modal-overlay {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
}
.rm-add-cond .url-help-modal-panel {
  position: relative;
  max-width: 560px;
  width: calc(100% - 40px);
  max-height: calc(100vh - 60px);
  background: white;
  border-radius: 6px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.25);
}
.rm-add-cond .url-help-modal-header {
  background: var(--brand);
  color: white;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}
.rm-add-cond .url-help-modal-title {
  font-size: 15px;
  font-weight: 700;
}
.rm-add-cond .url-help-modal-close {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
}
.rm-add-cond .url-help-modal-close:hover {
  background: rgba(255, 255, 255, 0.15);
}
.rm-add-cond .url-help-modal-body {
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.rm-add-cond .url-help-item {
  border: 1px solid var(--line);
  border-radius: 4px;
  overflow: hidden;
}
.rm-add-cond .url-help-item-head {
  background: var(--surface-section);
  padding: 12px 16px;
  border-bottom: 1px solid var(--line);
}
.rm-add-cond .url-help-item-name {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 3px;
}
.rm-add-cond .url-help-item-desc {
  font-size: 12px;
  color: var(--text-soft);
  line-height: 1.5;
}
.rm-add-cond .url-help-item-examples {
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  font-size: 12px;
}
.rm-add-cond .url-help-setting {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}
.rm-add-cond .url-help-setting-label {
  color: var(--text-soft);
  font-family: inherit;
}
.rm-add-cond .url-help-chip {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  font-size: 12px;
}
.rm-add-cond .url-help-chip.brand {
  background: var(--brand-softer);
  color: var(--brand-deep);
  border: 1px solid #c5e4ec;
  font-weight: 600;
}
.rm-add-cond .url-help-chip.inline {
  padding: 0 2px;
  border: none;
  background: var(--brand-softer);
  color: var(--brand-deep);
}
.rm-add-cond .url-help-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.rm-add-cond .url-help-url {
  color: var(--text);
  font-family: 'SF Mono', Menlo, Consolas, monospace;
}
.rm-add-cond .url-help-match {
  font-size: 11px;
  font-family: inherit;
  white-space: nowrap;
}
.rm-add-cond .url-help-match.match {
  color: var(--brand-deep);
  font-weight: 500;
}
.rm-add-cond .url-help-match.nomatch {
  color: var(--text-faint);
  font-weight: 500;
}

/* 配信設定パターン適用前の確認モーダル。
   本文に「desc\n\n反映内容\n\n確認メッセージ」の複数段落を改行コードで渡すため、
   white-space: pre-line で段落区切りを可視化し、複数行リストも左寄せで読みやすくする。 */
.c-modal--pattern-confirm .c-modal__data {
  white-space: pre-line;
  text-align: left;
}
</style>
